// client/src/Game.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./Style.css";
import GameSessionHeader from "./GameSessionHeader";
import GameTutorialModal from "./GameTutorialModal";
import socket from "./socket";
import {
  formatTransactionCode,
  DEPOSIT_CODES,
  WITHDRAWAL_CODES,
  BOOK_CODES,
  RAKE_CODES,
  TIP_CODES,
  DEALER_PAYOUT_CODES,
} from "./utils/transactionUtils";

import {
  FaArrowDown,
  FaArrowUp,
  FaInfo,
  FaRegTrashAlt,
  FaCheck,
  FaDollarSign,
  FaTimes,
  FaArrowCircleUp,
  FaHourglassHalf,
  FaChair,
  FaPause,
  FaPlay,
  FaBell,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaCog,
} from "react-icons/fa";

const WaitlistTimer = ({ updatedAt, durationMinutes }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!updatedAt || !durationMinutes) {
      setTimeLeft(null);
      return;
    }

    const calculate = () => {
      const now = new Date();
      const updatedDate = new Date(updatedAt.replace(" ", "T"));
      const expiryDate = new Date(
        updatedDate.getTime() + durationMinutes * 60000,
      );
      const diff = expiryDate - now;

      if (diff <= 0) {
        setTimeLeft(0);
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${mins}:${secs < 10 ? "0" : ""}${secs}`);
      }
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [updatedAt, durationMinutes]);

  if (timeLeft === null) return null;

  if (timeLeft === 0) {
    return (
      <span
        className="game-player-badge game-player-badge--debt"
        style={{ marginLeft: "8px" }}
      >
        Expired
      </span>
    );
  }

  return (
    <span
      className="game-player-badge game-player-badge--running"
      style={{ marginLeft: "8px" }}
    >
      Held: {timeLeft}
    </span>
  );
};

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const input = phone.replace(/\D/g, "");
  if (input.length > 3 && input.length <= 6) {
    return `${input.slice(0, 3)}-${input.slice(3)}`;
  } else if (input.length > 6) {
    return `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`;
  }
  return input;
};

const getPhoneDigits = (phone) => String(phone || "").replace(/\D/g, "");

const getPlayerCodeDigits = (code) => String(code || "").replace(/\D/g, "");

const normalizePlayerSearchValue = (value) =>
  String(value || "").toLowerCase().trim();

const getPlayerSearchText = (player = {}) =>
  [
    player.name,
    player.nickname,
    player.first_name,
    player.last_name,
    player.firstName,
    player.lastName,
  ]
    .map(normalizePlayerSearchValue)
    .filter(Boolean)
    .join(" ");

const getPlayerFullName = (player = {}) =>
  [player.first_name || player.firstName, player.last_name || player.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const getDefaultPlayerNickname = (firstName = "", lastName = "") => {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  if (!first) return "Nickname";
  return last ? `${first} ${last.slice(0, 2)}` : first;
};

const filterPlayerOption = ({ data }, inputValue) => {
  const searchValue = normalizePlayerSearchValue(inputValue);
  if (!searchValue) return true;
  return data.searchText?.includes(searchValue);
};

const formatPlayerOptionLabel = ({ label, fullName }) => {
  const showFullName =
    fullName &&
    normalizePlayerSearchValue(fullName) !== normalizePlayerSearchValue(label);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "0.4rem",
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontWeight: 700,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {showFullName && (
        <span
          style={{
            color: "#a0aec0",
            fontSize: "0.82rem",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          ({fullName})
        </span>
      )}
    </div>
  );
};

const getPlayerDisplayName = (player = {}) =>
  String(player.name || getPlayerFullName(player) || "Unnamed player").trim();

const getReferralPlayerOption = (player = {}) => {
  const label = getPlayerDisplayName(player);
  const fullName = getPlayerFullName(player);
  return {
    value: player.id,
    label,
    fullName,
    searchText: [label, fullName]
      .map(normalizePlayerSearchValue)
      .filter(Boolean)
      .join(" "),
  };
};

const isPlayerReferralLocked = (player = {}) =>
  Number(player.deposits_count || 0) > 0 ||
  Number(player.total_deposits || 0) > 0;

export default function Game() {
  // ============================================================================
  // ROUTING & AUTH
  // ============================================================================
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [tables, setTables] = useState([]);
  const [players, setPlayers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [books, setBooks] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [activeDealers, setActiveDealers] = useState([]);

  const [tablePlayers, setTablePlayers] = useState({});
  const [tableWaitlists, setTableWaitlists] = useState({});
  const [tableRsvps, setTableRsvps] = useState({});

  const [currSession, setCurrSession] = useState(null);

  // ============================================================================
  // UI STATE
  // ============================================================================
  const [openTables, setOpenTables] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTutorialModal, setShowTutorialModal] = useState(false);

  // ============================================================================
  // FORM STATE - Add Table
  // ============================================================================
  const [gameType, setGameType] = useState("");
  const [stakes, setStakes] = useState("");
  const [description, setDescription] = useState("");
  const [maxPlayers, setMaxPlayers] = useState(null);
  const [defaultMaxPlayers, setDefaultMaxPlayers] = useState(null);
  const [waitlistDuration, setWaitlistDuration] = useState(null);
  const [badBeatEnabled, setBadBeatEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [bombs, setBombs] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [registrationTime, setRegistrationTime] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ============================================================================
  // FORM STATE - Seating & Dealers
  // ============================================================================
  const [selectedPlayerToAddId, setSelectedPlayerToAddId] = useState({});
  const [comboForm, setComboForm] = useState({});

  // ============================================================================
  // MODAL STATE - Transaction Modal
  // ============================================================================
  const [txnForm, setTxnForm] = useState({
    tableId: null,
    playerId: null,
    fromX: false,
  });

  const [txnInputs, setTxnInputs] = useState({
    type: "deposit",
    credit: false,
    amount: "",
    method: "cash",
    settle: true,
    receiver: "",
    payLater: true,
    splits: [],
    leaving: false,
  });

  // ============================================================================
  // MODAL STATE - Other Modals
  // ============================================================================
  const [movingPlayer, setMovingPlayer] = useState(null);
  const [targetTableId, setTargetTableId] = useState(null);
  const [clearingPlayer, setClearingPlayer] = useState(null);
  const [clearingDebtType, setClearingDebtType] = useState("session"); // "session" or "book"

  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPlayerTransactions, setSelectedPlayerTransactions] = useState(
    [],
  );
  const [notesPlayer, setNotesPlayer] = useState(null);

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableTransactions, setSelectedTableTransactions] = useState(
    [],
  );

  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [selectedDealerTransactions, setSelectedDealerTransactions] = useState(
    [],
  );

  // ============================================================================
  // MODAL STATE - Pay Dealer
  // ============================================================================
  const [payingDealer, setPayingDealer] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payReceiver, setPayReceiver] = useState("");
  const [paySplits, setPaySplits] = useState([]);

  // ============================================================================
  // FORM STATE - Debt Clearing
  // ============================================================================
  const [debtAmounts, setDebtAmounts] = useState({});
  const [methodAmounts, setMethodAmounts] = useState({});
  const [clearReceivers, setClearReceivers] = useState({});

  // ============================================================================
  // FILTER STATE
  // ============================================================================
  const [txFilters, setTxFilters] = useState({
    rake: true,
    tips: true,
    deposit: true,
    withdrawal: true,
    book: true,
  });

  const [playerTxFilters, setPlayerTxFilters] = useState({
    book: true,
    deposit: true,
    withdrawal: true,
  });
  const [dealerTxFilters, setDealerTxFilters] = useState({
    rake: true,
    tips: true,
    payout: true,
  });

  // ============================================================================
  // MODAL STATE - Quick Add Player
  // ============================================================================
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [qFirstName, setQFirstName] = useState("");
  const [qLastName, setQLastName] = useState("");
  const [qNickname, setQNickname] = useState("");
  const [qPhone, setQPhone] = useState("");
  const [qBookId, setQBookId] = useState("house");
  const [qReferredByPlayerId, setQReferredByPlayerId] = useState("");

  // ============================================================================
  // MODAL STATE - Phone Lookup
  // ============================================================================
  const [showPhoneLookupModal, setShowPhoneLookupModal] = useState(false);
  const [phoneLookupDigits, setPhoneLookupDigits] = useState("");
  const phoneLookupInputRefs = useRef([]);
  const pendingPhoneLookupFocusIndex = useRef(null);

  // ============================================================================
  // MODAL STATE - Code Lookup
  // ============================================================================
  const [showCodeLookupModal, setShowCodeLookupModal] = useState(false);
  const [codeLookupDigits, setCodeLookupDigits] = useState("");
  const codeLookupInputRefs = useRef([]);
  const pendingCodeLookupFocusIndex = useRef(null);
  const txnAmountInputRef = useRef(null);
  const clearingAmountInputRef = useRef(null);
  const payDealerAmountInputRef = useRef(null);
  const bodyScrollLockRef = useRef(null);

  // ============================================================================
  // MODAL STATE - Edit Player
  // ============================================================================
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDebt, setEditDebt] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editBookId, setEditBookId] = useState("");
  const [editReferredByPlayerId, setEditReferredByPlayerId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [lookupSeatTableId, setLookupSeatTableId] = useState("");

  // ============================================================================
  // PENDING REQUESTS STATE
  // ============================================================================
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingRequestsCollapsed, setPendingRequestsCollapsed] =
    useState(false);
  const [linkingRequest, setLinkingRequest] = useState(null); // Request to link to existing
  const [linkingNewRequest, setLinkingNewRequest] = useState(null); // Request to link to new
  const [selectedExistingPlayerId, setSelectedExistingPlayerId] = useState("");
  const [newNickname, setNewNickname] = useState("");

  // ============================================================================
  // EFFECT: Auth & Axios Interceptor
  // ============================================================================
  useEffect(() => {
    if (permission?.includes("Y")) {
      navigate(`/${cardroom}/tournamentsHome`);
      return;
    }

    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [cardroom, dealerMode, navigate, permission]);

  // ============================================================================
  // EFFECT: Initial Data Load
  // ============================================================================
  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    async function loadData() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/cardroom`);
        setCurrSession(data.active);
        setDefaultMaxPlayers(data.default_max_players);
        setMaxPlayers(data.default_max_players);
        setWaitlistDuration(data.waitlistDuration);
        setBadBeatEnabled(data.bad_beat === 1 || data.badBeat === 1);

        if (data.lastSessionId === null && !data.tutorialModalDismissed) {
          setShowTutorialModal(true);
        }

        const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
        setTables(allTables);

        const { data: allPlayers } = await axios.get(
          `/${cardroom}/api/players`,
        );
        setPlayers(allPlayers);

        const { data: allDealers } = await axios.get(
          `/${cardroom}/api/dealers`,
        );
        setDealers(allDealers);

        const { data: active } = await axios.get(
          `/${cardroom}/api/players/active`,
        );
        setActivePlayers(active);

        const { data: activeDlrs } = await axios.get(
          `/${cardroom}/api/dealers/active`,
        );
        setActiveDealers(activeDlrs);

        const { data: allWallets } = await axios.get(
          `/${cardroom}/api/wallets`,
        );
        setWallets(allWallets);

        const { data: allBooks } = await axios.get(`/${cardroom}/api/books`);
        setBooks(allBooks);

        const { data: pending } = await axios.get(
          `/${cardroom}/api/pending-requests`,
        );
        setPendingRequests(pending);

        const seatedMapping = {};
        await Promise.all(
          allTables.map(async (tbl) => {
            try {
              const { data } = await axios.get(
                `/${cardroom}/api/tables/${tbl.id}/players`,
              );
              seatedMapping[tbl.id] = data;
            } catch {
              seatedMapping[tbl.id] = [];
            }
          }),
        );
        setTablePlayers(seatedMapping);

        const waitlistMapping = {};
        await Promise.all(
          allTables.map(async (tbl) => {
            try {
              const { data } = await axios.get(
                `/${cardroom}/api/tables/${tbl.id}/waitlist`,
              );
              waitlistMapping[tbl.id] = data;
            } catch {
              waitlistMapping[tbl.id] = [];
            }
          }),
        );
        setTableWaitlists(waitlistMapping);

        const rsvpMapping = {};
        await Promise.all(
          allTables.map(async (tbl) => {
            try {
              const { data } = await axios.get(
                `/${cardroom}/api/tables/${tbl.id}/rsvps`,
              );
              rsvpMapping[tbl.id] = data;
            } catch {
              rsvpMapping[tbl.id] = [];
            }
          }),
        );
        setTableRsvps(rsvpMapping);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/`);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [token, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Socket.IO Event Listeners
  // ============================================================================
  useEffect(() => {
    if (!cardroom) return;

    socket.io.opts.query = { cardroom };
    socket.connect();

    const handlePlayerRemoved = async ({
      tableId,
      playerName,
      tableDescription,
    }) => {
      try {
        const { data: players } = await axios.get(
          `/${cardroom}/api/tables/${tableId}/players`,
        );
        setTablePlayers((tp) => ({ ...tp, [tableId]: players }));

        // Only refresh waitlist if timers are enabled (as requested for efficiency)
        if (waitlistDuration) {
          const { data: wl } = await axios.get(
            `/${cardroom}/api/tables/${tableId}/waitlist`,
          );
          setTableWaitlists((prev) => ({ ...prev, [tableId]: wl }));

          const { data: allTables } = await axios.get(
            `/${cardroom}/api/tables`,
          );
          setTables(allTables);
        }
      } catch (err) {
        console.error("Error reloading data from socket", err);
      }
    };

    const handleTransactionAdded = async ({
      tableId,
      playerId,
      playerName,
      amount,
      credit,
      method,
      tableDescription,
      type,
    }) => {
      const verb = type === "withdrawal" ? "Withdrawal" : "Deposit";
      const msg = `${verb} of $${amount} for ${playerName} at ${
        tableDescription || `Table ${tableId}`
      } (${credit ? "credit" : method})`;
      setNotifications((prev) => [{ id: Date.now(), message: msg }, ...prev]);

      if (credit || type === "deposit") {
        const { data } = await axios.get(
          `/${cardroom}/api/tables/${tableId}/players`,
        );
        setTablePlayers((tp) => ({ ...tp, [tableId]: data }));
      }

      const { data: active } = await axios.get(
        `/${cardroom}/api/players/active`,
      );
      setActivePlayers(active);

      if (selectedTableId != null) listTableTransactions(tableId);
      if (selectedPlayerId != null) listPlayerTransactions(playerId);
    };

    const handleWaitlistRequest = ({ name }) => {
      setNotifications((prev) => [
        { id: Date.now(), message: `${name} requested a seat!` },
        ...prev,
      ]);
    };

    const handleChipsRequest = ({ name, chips, table }) => {
      const tableStr = table ? ` at ${table}` : "";
      setNotifications((prev) => [
        {
          id: Date.now(),
          message: `${name} $${chips} requested ${tableStr}`,
        },
        ...prev,
      ]);
    };

    const handleNewPendingRequest = async () => {
      try {
        const { data } = await axios.get(`/${cardroom}/api/pending-requests`);
        setPendingRequests(data);
        setNotifications((prev) => [
          { id: Date.now(), message: "New mobile join request received!" },
          ...prev,
        ]);
      } catch (err) {
        console.error("Error refreshing pending requests", err);
      }
    };

    const handleTableUpdate = async ({
      tableId,
      playerName,
      type,
      tableDescription,
    }) => {
      const target = type === "rsvp" ? "RSVP list" : "waitlist";
      const msg = `${playerName} joined the ${target} for ${tableDescription || `Table ${tableId}`}`;
      setNotifications((prev) => [{ id: Date.now(), message: msg }, ...prev]);

      try {
        // Refresh waitlist or RSVP
        if (type === "waitlist") {
          const { data } = await axios.get(
            `/${cardroom}/api/tables/${tableId}/waitlist`,
          );
          setTableWaitlists((prev) => ({ ...prev, [tableId]: data }));
        } else if (type === "rsvp") {
          const { data } = await axios.get(
            `/${cardroom}/api/tables/${tableId}/rsvps`,
          );
          setTableRsvps((prev) => ({ ...prev, [tableId]: data }));
        }

        // Also refresh the main tables info (to update playing/waiting/rsvp counts)
        const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
        setTables(allTables);
      } catch (err) {
        console.error("Error reloading table data from socket", err);
      }
    };

    socket.on("playerRemoved", handlePlayerRemoved);
    socket.on("transactionAdded", handleTransactionAdded);
    socket.on("waitlistRequest", handleWaitlistRequest);
    socket.on("chips_request", handleChipsRequest);
    socket.on("newPendingRequest", handleNewPendingRequest);
    socket.on("tableUpdate", handleTableUpdate);

    return () => {
      socket.off("playerRemoved", handlePlayerRemoved);
      socket.off("transactionAdded", handleTransactionAdded);
      socket.off("waitlistRequest", handleWaitlistRequest);
      socket.off("chips_request", handleChipsRequest);
      socket.off("newPendingRequest", handleNewPendingRequest);
      socket.off("tableUpdate", handleTableUpdate);
      socket.disconnect();
    };
  }, [cardroom, selectedTableId, selectedPlayerId]);

  // ============================================================================
  // EFFECT: Prevent body scroll when modal is open
  // ============================================================================
  // Disabled if: display name taken, OR first+last name pair already exists
  const quickAddBtnDisabled = (() => {
    if (!qFirstName.trim()) return true;
    const enteredFirst = qFirstName.trim().toLowerCase();
    const enteredLast = qLastName.trim().toLowerCase();
    const enteredDisplay = qNickname.trim().toLowerCase();
    return (players || []).some((p) => {
      // Check display name collision
      if (enteredDisplay && p.name?.toLowerCase() === enteredDisplay)
        return true;
      // Check first+last pair collision
      if (
        p.first_name?.toLowerCase() === enteredFirst &&
        p.last_name?.toLowerCase() === enteredLast
      )
        return true;
      return false;
    });
  })();

  const referralPlayerOptions = (players || []).map(getReferralPlayerOption);
  const editReferralPlayerOptions = referralPlayerOptions.filter(
    (option) => Number(option.value) !== Number(editingPlayer?.id),
  );
  const referralLocked = isPlayerReferralLocked(editingPlayer || {});

  const phoneLookupMatch =
    phoneLookupDigits.length === 10
      ? (players || []).find(
          (player) => getPhoneDigits(player.phone_number) === phoneLookupDigits,
        )
      : null;

  const codeLookupMatch =
    codeLookupDigits.length === 5
      ? (players || []).find(
          (player) =>
            getPlayerCodeDigits(player.player_code) === codeLookupDigits,
        )
      : null;

  const allSeatedPlayerIds = Object.values(tablePlayers).reduce((set, arr) => {
    arr.forEach((player) => set.add(player.id));
    return set;
  }, new Set());

  const preventModalNumberWheel = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
  };

  const selectAmountInput = (ref) => {
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.select();
    });
  };

  const defaultTxnMethodFor = (type, credit = false) =>
    type === "bonus" || credit ? "" : "cash";

  const isAnyModalOpen =
    showTutorialModal ||
    selectedPlayerId !== null ||
    selectedDealerId !== null ||
    selectedTableId !== null ||
    movingPlayer !== null ||
    clearingPlayer !== null ||
    notesPlayer !== null ||
    payingDealer !== null ||
    showQuickAddModal ||
    showPhoneLookupModal ||
    showCodeLookupModal ||
    editingPlayer !== null ||
    linkingRequest !== null ||
    linkingNewRequest !== null ||
    (txnForm.tableId !== null && txnForm.playerId !== null);

  const unlockBodyScroll = () => {
    const lock = bodyScrollLockRef.current;
    if (!lock) return;

    document.body.style.overflow = lock.bodyOverflow;
    document.body.style.position = lock.bodyPosition;
    document.body.style.top = lock.bodyTop;
    document.body.style.left = lock.bodyLeft;
    document.body.style.right = lock.bodyRight;
    document.body.style.width = lock.bodyWidth;
    document.documentElement.style.overflow = lock.htmlOverflow;
    window.scrollTo(0, lock.scrollY);
    bodyScrollLockRef.current = null;
  };

  const lockBodyScroll = () => {
    if (bodyScrollLockRef.current) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    bodyScrollLockRef.current = {
      scrollY,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      htmlOverflow: document.documentElement.style.overflow,
    };

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.documentElement.style.overflow = "hidden";
  };

  useEffect(() => {
    if (isAnyModalOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return () => {
      unlockBodyScroll();
    };
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (txnForm.tableId !== null && txnForm.playerId !== null) {
      selectAmountInput(txnAmountInputRef);
    }
  }, [txnForm.tableId, txnForm.playerId]);

  useEffect(() => {
    if (clearingPlayer) selectAmountInput(clearingAmountInputRef);
  }, [clearingPlayer]);

  useEffect(() => {
    if (payingDealer) selectAmountInput(payDealerAmountInputRef);
  }, [payingDealer]);

  useEffect(() => {
    if (pendingPhoneLookupFocusIndex.current === null) return;
    phoneLookupInputRefs.current[pendingPhoneLookupFocusIndex.current]?.focus();
    pendingPhoneLookupFocusIndex.current = null;
  }, [phoneLookupDigits]);

  useEffect(() => {
    if (pendingCodeLookupFocusIndex.current === null) return;
    codeLookupInputRefs.current[pendingCodeLookupFocusIndex.current]?.focus();
    pendingCodeLookupFocusIndex.current = null;
  }, [codeLookupDigits]);

  // ============================================================================
  // HANDLERS: Table Management
  // ============================================================================

  const addTable = async (e) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/tables`, {
        gameType: gameType || null,
        stakes: stakes || null,
        name: description || null,
        maxPlayers: maxPlayers,
        scheduledAt: scheduledAt || null,
        bombs: bombs,
        public: isPublic ? 1 : 0,
        open: isOpen ? 1 : 0,
        registrationTime:
          isPublic && registrationTime ? registrationTime : null,
      });

      const { data: freshTables } = await axios.get(`/${cardroom}/api/tables`);
      setTables(freshTables);

      setGameType("");
      setStakes("");
      setDescription("");
      setMaxPlayers(defaultMaxPlayers);
      setScheduledAt("");
      setBombs(false);
      setIsPublic(true);
      setIsOpen(true);
      setRegistrationTime("");
    } catch (err) {
      console.error("Add table failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTableSettings = async (tableId, data) => {
    try {
      await axios.patch(`/${cardroom}/api/tables/${tableId}`, data);
      const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
      setTables(allTables);
    } catch (err) {
      console.error("Update table settings failed", err);
      alert(err.response?.data?.error || "Could not update table");
    }
  };

  const approveRequest = async (requestId) => {
    try {
      const req = pendingRequests.find((r) => r.id === requestId);
      await axios.post(
        `/${cardroom}/api/pending-requests/${requestId}/confirm`,
      );

      const { data: pending } = await axios.get(
        `/${cardroom}/api/pending-requests`,
      );
      setPendingRequests(pending);

      // Reload tables to show new player on waitlist
      const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
      setTables(allTables);

      // Reload waitlist and RSVPs for the affected table
      if (req) {
        const { data: wl } = await axios.get(
          `/${cardroom}/api/tables/${req.table_id}/waitlist`,
        );
        setTableWaitlists((prev) => ({ ...prev, [req.table_id]: wl }));

        const { data: rsvps } = await axios.get(
          `/${cardroom}/api/tables/${req.table_id}/rsvps`,
        );
        setTableRsvps((prev) => ({ ...prev, [req.table_id]: rsvps }));
      }
    } catch (err) {
      console.error("Approve request failed", err);
      alert(err.response?.data?.error || "Could not approve request");
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.delete(`/${cardroom}/api/pending-requests/${requestId}`);
      const { data: pending } = await axios.get(
        `/${cardroom}/api/pending-requests`,
      );
      setPendingRequests(pending);
    } catch (err) {
      console.error("Reject request failed", err);
    }
  };

  const linkExisting = async (e) => {
    e.preventDefault();
    if (!selectedExistingPlayerId) return;
    try {
      await axios.patch(
        `/${cardroom}/api/pending-requests/${linkingRequest.id}/link-existing`,
        {
          playerId: selectedExistingPlayerId,
        },
      );
      setLinkingRequest(null);
      setSelectedExistingPlayerId("");
      const { data: pending } = await axios.get(
        `/${cardroom}/api/pending-requests`,
      );
      setPendingRequests(pending);
    } catch (err) {
      console.error("Link existing failed", err);
    }
  };

  const linkNew = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `/${cardroom}/api/pending-requests/${linkingNewRequest.id}/link-new`,
        {
          nickname: newNickname,
          firstName: linkingNewRequest.first_name,
          lastName: linkingNewRequest.last_name,
          phone: linkingNewRequest.phone_number,
        },
      );
      setLinkingNewRequest(null);
      setNewNickname("");
      const { data: pending } = await axios.get(
        `/${cardroom}/api/pending-requests`,
      );
      setPendingRequests(pending);
      // Reload all players
      const { data: allPlayers } = await axios.get(`/${cardroom}/api/players`);
      setPlayers(allPlayers);
    } catch (err) {
      console.error("Link new failed", err);
    }
  };

  const handleDeleteTable = async (tableId) => {
    try {
      await axios.delete(`/${cardroom}/api/tables/${tableId}`);
      setTables((prev) => prev.filter((t) => t.id !== tableId));
    } catch (err) {
      alert(err.response?.data?.error || "Could not delete table");
    }
  };

  const handleTableTime = async (tableId, isRunning) => {
    try {
      await axios.post(`/${cardroom}/api/tables/${tableId}/time`, {
        pausing: isRunning,
      });
      const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
      setTables(allTables);
    } catch (err) {
      console.error("Table time control error", err);
    }
  };

  // ============================================================================
  // HANDLERS: Player Seating
  // ============================================================================

  const seatPlayer = async (tableId, playerId, showTransactionModal = true) => {
    try {
      await axios.post(`/${cardroom}/api/tables/${tableId}/players`, {
        playerId,
      });

      const [playersRes, waitsRes, rsvpsRes] = await Promise.all([
        axios.get(`/${cardroom}/api/tables/${tableId}/players`),
        axios.get(`/${cardroom}/api/tables/${tableId}/waitlist`),
        axios.get(`/${cardroom}/api/tables/${tableId}/rsvps`),
      ]);

      setTablePlayers((tp) => ({ ...tp, [tableId]: playersRes.data }));
      setTableWaitlists((wl) => ({ ...wl, [tableId]: waitsRes.data }));
      setTableRsvps((r) => ({ ...r, [tableId]: rsvpsRes.data }));

      if (showTransactionModal) {
        setTxnForm({ tableId, playerId, fromX: false });
        setTxnInputs({
          type: "deposit",
          credit: false,
          amount: "",
          method: "cash",
          settle: false,
          receiver: "",
          payLater: true,
          splits: [],
        });
      }

      const { data: active } = await axios.get(
        `/${cardroom}/api/players/active`,
      );
      setActivePlayers(active);
    } catch (err) {
      console.error("Seat player failed", err);
    }
  };

  const unseatPlayer = async (tableId, playerId) => {
    try {
      await axios.delete(
        `/${cardroom}/api/tables/${tableId}/unseat/${playerId}?notify=1`,
      );
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/players`,
      );
      setTablePlayers((tp) => ({ ...tp, [tableId]: data }));

      if (waitlistDuration) {
        const { data: wl } = await axios.get(
          `/${cardroom}/api/tables/${tableId}/waitlist`,
        );
        setTableWaitlists((prev) => ({ ...prev, [tableId]: wl }));
      }
    } catch (err) {
      console.error("Unseat player failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: Pay Dealer
  // ============================================================================
  const handlePayDealer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      let payload;

      if (payMethod === "mixed") {
        payload = {
          dealerId: payingDealer.id,
          method: "mixed",
          splits: (paySplits || [])
            .filter((s) => Number(s.amount) > 0 && s.method)
            .map((s) => ({
              amount: Number(s.amount),
              method: s.method,
              receiverId: s.receiver || null,
            })),
        };
      } else {
        payload = {
          dealerId: payingDealer.id,
          amount: Number(payAmount || 0),
          method: payMethod,
          receiverId: payReceiver || null,
        };
      }

      const { data } = await axios.post(
        `/${cardroom}/api/dealers/pay`,
        payload,
      );

      // Update dealers local state
      const updatedOwed = data?.newTips ?? 0;
      setDealers((prev) =>
        prev.map((d) =>
          d.id === payingDealer.id ? { ...d, owed: updatedOwed } : d,
        ),
      );
      setActiveDealers((prev) =>
        prev.map((d) =>
          d.id === payingDealer.id ? { ...d, owed: updatedOwed } : d,
        ),
      );

      setPayingDealer(null);
      setPayAmount("");
      setPayMethod("cash");
      setPayReceiver("");
      setPaySplits([]);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error paying dealer");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Quick Add Player
  // ============================================================================
  const resetQuickAddPlayerForm = () => {
    setQFirstName("");
    setQLastName("");
    setQNickname("");
    setQPhone("");
    setQBookId("house");
    setQReferredByPlayerId("");
  };

  const closeQuickAddPlayerModal = () => {
    setShowQuickAddModal(false);
    resetQuickAddPlayerForm();
  };

  const handleQuickAddPlayer = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!qFirstName.trim()) {
      alert("Enter a first name");
      return;
    }

    const digitsOnlyPhone = qPhone.replace(/\D/g, "");
    if (digitsOnlyPhone.length !== 10 && digitsOnlyPhone.length !== 0) {
      alert("Enter a valid 10-digit phone number");
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: player } = await axios.post(`/${cardroom}/api/players`, {
        firstName: qFirstName.trim(),
        lastName: qLastName.trim(),
        name: qNickname.trim(),
        phone: digitsOnlyPhone,
        bookId: qBookId === "house" ? null : Number(qBookId),
        referredByPlayerId: qReferredByPlayerId
          ? Number(qReferredByPlayerId)
          : null,
      });

      // Update the main players list so they shows up in seating dropdowns
      setPlayers((prev) => [...prev, player]);

      // Reset and close
      closeQuickAddPlayerModal();
    } catch (err) {
      alert(err.response?.data?.error || "Could not add player");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closePhoneLookupModal = () => {
    setShowPhoneLookupModal(false);
    setPhoneLookupDigits("");
    setLookupSeatTableId("");
  };

  const handlePhoneLookupDigitChange = (index, rawValue) => {
    const enteredDigits = getPhoneDigits(rawValue);
    if (!enteredDigits) {
      setPhoneLookupDigits((prev) => {
        const chars = prev.split("");
        chars[index] = "";
        return chars.join("").slice(0, 10);
      });
      return;
    }

    setPhoneLookupDigits((prev) => {
      const targetIndex = Math.min(index, prev.length);
      const chars = prev.split("");
      enteredDigits.split("").forEach((digit, offset) => {
        if (targetIndex + offset < 10) {
          chars[targetIndex + offset] = digit;
        }
      });
      pendingPhoneLookupFocusIndex.current = Math.min(
        targetIndex + enteredDigits.length,
        9,
      );
      return chars.join("").slice(0, 10);
    });
  };

  const handlePhoneLookupDigitFocus = (index) => {
    if (index > phoneLookupDigits.length) {
      phoneLookupInputRefs.current[phoneLookupDigits.length]?.focus();
    }
  };

  const handlePhoneLookupDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !phoneLookupDigits[index] && index > 0) {
      phoneLookupInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePhoneLookupPaste = (index, e) => {
    e.preventDefault();
    handlePhoneLookupDigitChange(index, e.clipboardData.getData("text"));
  };

  const closeCodeLookupModal = () => {
    setShowCodeLookupModal(false);
    setCodeLookupDigits("");
    setLookupSeatTableId("");
  };

  const handleCodeLookupDigitChange = (index, rawValue) => {
    const enteredDigits = getPlayerCodeDigits(rawValue);
    if (!enteredDigits) {
      setCodeLookupDigits((prev) => {
        const chars = prev.split("");
        chars[index] = "";
        return chars.join("").slice(0, 5);
      });
      return;
    }

    setCodeLookupDigits((prev) => {
      const targetIndex = Math.min(index, prev.length);
      const chars = prev.split("");
      enteredDigits.split("").forEach((digit, offset) => {
        if (targetIndex + offset < 5) {
          chars[targetIndex + offset] = digit;
        }
      });
      pendingCodeLookupFocusIndex.current = Math.min(
        targetIndex + enteredDigits.length,
        4,
      );
      return chars.join("").slice(0, 5);
    });
  };

  const handleCodeLookupDigitFocus = (index) => {
    if (index > codeLookupDigits.length) {
      codeLookupInputRefs.current[codeLookupDigits.length]?.focus();
    }
  };

  const handleCodeLookupDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeLookupDigits[index] && index > 0) {
      codeLookupInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodeLookupPaste = (index, e) => {
    e.preventDefault();
    handleCodeLookupDigitChange(index, e.clipboardData.getData("text"));
  };

  const resetEditPlayerForm = () => {
    setEditingPlayer(null);
    setEditFirstName("");
    setEditLastName("");
    setEditDisplayName("");
    setEditDebt("");
    setEditPhone("");
    setEditBookId("");
    setEditReferredByPlayerId("");
    setEditNotes("");
  };

  const openEditPlayerModal = (player) => {
    setEditingPlayer(player);
    setEditFirstName(player.first_name || "");
    setEditLastName(player.last_name || "");
    setEditDisplayName("");
    setEditPhone(formatPhoneNumber(player.phone_number));
    setEditBookId(player.book_id ? String(player.book_id) : "house");
    setEditReferredByPlayerId(
      player.referred_by_player_id ? String(player.referred_by_player_id) : "",
    );
    setEditNotes(player.notes || "");
    setShowPhoneLookupModal(false);
    setShowCodeLookupModal(false);
    setLookupSeatTableId("");
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    if (isSubmitting || !editingPlayer) return;

    const digitsOnlyPhone = editPhone.replace(/\D/g, "");
    if (digitsOnlyPhone.length !== 10 && digitsOnlyPhone.length !== 0) {
      alert("Enter a valid 10-digit phone number or leave blank");
      return;
    }

    try {
      setIsSubmitting(true);
      const editReferralLocked = isPlayerReferralLocked(editingPlayer);
      const response = await axios.post(
        `/${cardroom}/api/players/edit/${editingPlayer.id}`,
        {
          firstName: editFirstName.trim() || undefined,
          lastName:
            editLastName !== undefined ? editLastName.trim() : undefined,
          name: editDisplayName.trim() || undefined,
          book: editDebt === "" ? "" : Number(editDebt),
          phone: digitsOnlyPhone || "",
          bookId:
            editBookId === ""
              ? ""
              : editBookId === "house"
                ? null
                : Number(editBookId),
          referredByPlayerId: editReferralLocked
            ? undefined
            : editReferredByPlayerId
              ? Number(editReferredByPlayerId)
              : null,
          notes: editNotes,
        },
      );

      setPlayers((prev) =>
        prev.map((player) =>
          player.id === editingPlayer.id ? response.data.player : player,
        ),
      );
      setTablePlayers((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([tableId, tablePlayersForTable]) => [
            tableId,
            tablePlayersForTable.map((player) =>
              player.id === editingPlayer.id
                ? { ...player, ...response.data.player }
                : player,
            ),
          ]),
        ),
      );
      setTableWaitlists((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([tableId, waitlist]) => [
            tableId,
            waitlist.map((player) =>
              player.id === editingPlayer.id
                ? { ...player, ...response.data.player }
                : player,
            ),
          ]),
        ),
      );
      setTableRsvps((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([tableId, rsvps]) => [
            tableId,
            rsvps.map((player) =>
              player.id === editingPlayer.id
                ? { ...player, ...response.data.player }
                : player,
            ),
          ]),
        ),
      );

      resetEditPlayerForm();
    } catch (err) {
      alert(err.response?.data?.error || "Could not update player");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLookupSeatPlayer = async (player) => {
    const tableId = Number(lookupSeatTableId);
    if (!player || !tableId) return;
    if (allSeatedPlayerIds.has(player.id)) {
      alert("This player is already seated.");
      return;
    }

    setShowPhoneLookupModal(false);
    setShowCodeLookupModal(false);
    setPhoneLookupDigits("");
    setCodeLookupDigits("");
    setLookupSeatTableId("");
    await seatPlayer(tableId, player.id);
  };

  const renderLookupPlayerActions = (player) => {
    const seatableTables = tables.filter((table) => table.start_time);
    const isPlayerSeated = allSeatedPlayerIds.has(player.id);

    return (
      <div
        style={{
          display: "grid",
          gap: "0.75rem",
          marginTop: "1rem",
        }}
      >
        {permission?.includes("P") && (
          <button
            type="button"
            className="game-btn game-btn--secondary"
            onClick={() => openEditPlayerModal(player)}
          >
            <FaCog /> Edit Player
          </button>
        )}

        <div
          className="game-form-row"
          style={{
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <select
            className="game-input"
            value={lookupSeatTableId}
            onChange={(e) => setLookupSeatTableId(e.target.value)}
            disabled={isPlayerSeated || !currSession}
            style={{ flex: 1, minWidth: "180px" }}
          >
            <option value="">Select table</option>
            {seatableTables.map((table) => (
              <option key={table.id} value={table.id}>
                {table.name ? `${table.name} - ` : ""}
                {table.stakes} {table.game_type}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="game-btn game-btn--primary"
            onClick={() => handleLookupSeatPlayer(player)}
            disabled={
              !lookupSeatTableId ||
              isPlayerSeated ||
              !currSession ||
              seatableTables.length === 0
            }
          >
            <FaChair /> Seat
          </button>
        </div>

        {isPlayerSeated && (
          <p className="phone-lookup-status">This player is already seated.</p>
        )}
        {!isPlayerSeated && !currSession && (
          <p className="phone-lookup-status">
            Start a session to seat players.
          </p>
        )}
      </div>
    );
  };

  // ============================================================================
  // HANDLERS: Waitlist Management
  // ============================================================================

  const addToWaitlist = async (tableId, playerId) => {
    try {
      await axios.post(`/${cardroom}/api/tables/${tableId}/waitlist`, {
        playerId,
      });
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/waitlist`,
      );
      setTableWaitlists((w) => ({ ...w, [tableId]: data }));
    } catch (err) {
      console.error("Add to waitlist failed", err);
    }
  };

  const removeFromWaitlist = async (tableId, playerId) => {
    try {
      await axios.delete(
        `/${cardroom}/api/tables/${tableId}/waitlist/${playerId}`,
      );
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/waitlist`,
      );
      setTableWaitlists((w) => ({ ...w, [tableId]: data }));
    } catch (err) {
      console.error("Remove from waitlist failed", err);
    }
  };

  const moveUpInWaitlist = async (tableId, playerId) => {
    try {
      await axios.patch(
        `/${cardroom}/api/tables/${tableId}/waitlist/${playerId}/move-up`,
      );
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/waitlist`,
      );
      setTableWaitlists((w) => ({ ...w, [tableId]: data }));
    } catch (err) {
      console.error("Move up in waitlist failed", err);
    }
  };

  const moveDownInWaitlist = async (tableId, playerId) => {
    try {
      await axios.patch(
        `/${cardroom}/api/tables/${tableId}/waitlist/${playerId}/move-down`,
      );
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/waitlist`,
      );
      setTableWaitlists((w) => ({ ...w, [tableId]: data }));
    } catch (err) {
      console.error("Move down in waitlist failed", err);
    }
  };

  const moveUpFromWaitlist = async (tableId, playerId) => {
    try {
      await axios.post(`/${cardroom}/api/tables/${tableId}/players`, {
        playerId,
      });

      const [playersRes, waitRes] = await Promise.all([
        axios.get(`/${cardroom}/api/tables/${tableId}/players`),
        axios.get(`/${cardroom}/api/tables/${tableId}/waitlist`),
      ]);

      setTableWaitlists((wl) => ({ ...wl, [tableId]: waitRes.data }));
      setTablePlayers((tp) => ({ ...tp, [tableId]: playersRes.data }));

      setTxnForm({ tableId, playerId, fromX: false });
      setTxnInputs({
        type: "deposit",
        credit: false,
        amount: "",
        method: "cash",
        receiver: "",
        settle: false,
        payLater: true,
        splits: [],
        leaving: false,
      });
    } catch (err) {
      console.error("Move up from waitlist failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: RSVP Management
  // ============================================================================

  const rsvpPlayer = async (tableId, playerId) => {
    try {
      await axios.post(`/${cardroom}/api/tables/${tableId}/rsvp`, {
        playerId,
      });
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/rsvps`,
      );
      setTableRsvps((r) => ({ ...r, [tableId]: data }));
    } catch (err) {
      console.error("Add to RSVP failed", err);
    }
  };

  const removeFromRsvp = async (tableId, playerId) => {
    try {
      await axios.delete(`/${cardroom}/api/tables/${tableId}/rsvp/${playerId}`);
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/rsvps`,
      );
      setTableRsvps((r) => ({ ...r, [tableId]: data }));
    } catch (err) {
      console.error("Remove from RSVP failed", err);
    }
  };

  const moveFromRsvpToWaitlist = async (tableId, playerId) => {
    try {
      await axios.post(
        `/${cardroom}/api/tables/${tableId}/waitlist/from-rsvp`,
        {
          playerId,
        },
      );
      const [waitRes, rsvpRes] = await Promise.all([
        axios.get(`/${cardroom}/api/tables/${tableId}/waitlist`),
        axios.get(`/${cardroom}/api/tables/${tableId}/rsvps`),
      ]);
      setTableWaitlists((wl) => ({ ...wl, [tableId]: waitRes.data }));
      setTableRsvps((r) => ({ ...r, [tableId]: rsvpRes.data }));
    } catch (err) {
      console.error("Move from RSVP to waitlist failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: Transactions (Deposits/Withdrawals)
  // ============================================================================

  const submitTransaction = async ({
    tableId,
    playerId,
    type,
    credit,
    amount,
    method,
    settle,
    receiver,
    splits,
    leaving = false,
  }) => {
    try {
      const safeReceiver = !receiver || credit ? null : receiver;

      const { data: txnResponse } = await axios.post(
        `/${cardroom}/api/transactions`,
        {
          tableId,
          playerId,
          type,
          credit,
          amount: parseFloat(amount),
          method,
          settle,
          walletId: safeReceiver,
          splits,
        },
      );

      const wasBook = txnInputs.method === "book";
      const isBonus = type === "bonus";

      if (isBonus) {
        const bonusPlayerName =
          modalPlayer?.name || modalPlayer?.first_name || "Player";
        setNotifications((prev) => [
          {
            id: Date.now(),
            message: `$${amount} bonus recorded for ${bonusPlayerName}`,
          },
          ...prev,
        ]);
      }

      // Show notifications for first-time / referral bonuses applied during deposit
      if (txnResponse?.appliedBonuses?.length > 0) {
        const bonusPlayerName =
          modalPlayer?.name || modalPlayer?.first_name || "Player";
        txnResponse.appliedBonuses.forEach((b, i) => {
          const delay = i * 50; // slight offset so IDs don't collide
          setTimeout(() => {
            let msg;
            if (b.type === "first_time") {
              msg = `$${b.amount} first-time bonus applied for ${bonusPlayerName}`;
            } else if (b.type === "referee") {
              msg = `$${b.amount} referee bonus applied for ${bonusPlayerName}`;
            } else if (b.type === "referrer") {
              msg = `$${b.amount} referral bonus applied for ${b.playerName || "referrer"}`;
            }
            if (msg) {
              setNotifications((prev) => [
                { id: Date.now(), message: msg },
                ...prev,
              ]);
            }
          }, delay);
        });
      }

      if (leaving) {
        unseatPlayer(tableId, playerId);
      }

      if (type === "withdrawal") {
        const { data } = await axios.get(
          `/${cardroom}/api/tables/${tableId}/players`,
        );
        setTablePlayers((tp) => ({ ...tp, [tableId]: data }));
      }

      setTxnForm({ tableId: null, playerId: null });
      setTxnInputs({
        type: "deposit",
        credit: false,
        amount: "",
        method: "cash",
        settle: false,
        receiver: "",
        payLater: true,
        splits: [],
        leaving: false,
      });

      if (credit || wasBook) {
        const { data } = await axios.get(
          `/${cardroom}/api/tables/${tableId}/players`,
        );
        setTablePlayers((tp) => ({ ...tp, [tableId]: data }));
      }

      const { data: active } = await axios.get(
        `/${cardroom}/api/players/active`,
      );
      setActivePlayers(active);

      setIsSubmitting(false);
    } catch (err) {
      console.error("Transaction submission failed", err);
      setIsSubmitting(false);
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const leaving = txnInputs.leaving;
    if (txnInputs.type === "debt") {
      await clearDebt(
        txnForm.playerId,
        txnInputs.amount,
        txnInputs.method,
        true,
        txnInputs.receiver,
        txnForm.tableId,
      );
      setTxnForm({ tableId: null, playerId: null });
      setTxnInputs({
        type: "deposit",
        credit: false,
        amount: "",
        method: "cash",
        settle: false,
        receiver: "",
        payLater: true,
        splits: [],
        leaving: false,
      });
      setIsSubmitting(false);
      return;
    }

    if (txnInputs.type === "account_balance") {
      // Paying off / paying out Account Debt (book), payingSessionCredit=false
      // When book < 0 (house owes player), amount must be negated so backend subtracts correctly
      const rawAmount = Number(txnInputs.amount);
      const finalAmount = modalRunning < 0 ? -rawAmount : rawAmount;
      await clearDebt(
        txnForm.playerId,
        finalAmount,
        txnInputs.method,
        false,
        txnInputs.receiver,
        txnForm.tableId,
      );
      setTxnForm({ tableId: null, playerId: null });
      setTxnInputs({
        type: "deposit",
        credit: false,
        amount: "",
        method: "cash",
        settle: false,
        receiver: "",
        payLater: true,
        splits: [],
        leaving: false,
      });
      setIsSubmitting(false);
      return;
    }

    if (txnInputs.type === "bonus" && txnInputs.method === "book") {
      try {
        await axios.post(`/${cardroom}/api/players/${txnForm.playerId}/bonus`, {
          amount: parseFloat(txnInputs.amount),
        });

        const bonusPlayerName =
          modalPlayer?.name || modalPlayer?.first_name || "Player";
        setNotifications((prev) => [
          {
            id: Date.now(),
            message: `$${txnInputs.amount} bonus recorded for ${bonusPlayerName}`,
          },
          ...prev,
        ]);

        if (txnForm.tableId) {
          const { data } = await axios.get(
            `/${cardroom}/api/tables/${txnForm.tableId}/players`,
          );
          setTablePlayers((tp) => ({ ...tp, [txnForm.tableId]: data }));
        }

        if (leaving) {
          unseatPlayer(txnForm.tableId, txnForm.playerId);
        }

        setTxnForm({ tableId: null, playerId: null });
        setTxnInputs({
          type: "deposit",
          credit: false,
          amount: "",
          method: "cash",
          settle: false,
          receiver: "",
          payLater: true,
          splits: [],
          leaving: false,
        });
      } catch (err) {
        console.error("Bonus to logic failed", err);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (txnInputs.amount > 0) {
      await submitTransaction({
        tableId: txnForm.tableId,
        playerId: txnForm.playerId,
        type: txnInputs.type,
        credit: txnInputs.credit,
        amount: txnInputs.amount,
        method: txnInputs.method,
        settle: txnInputs.settle,
        receiver: txnInputs.receiver,
        splits: txnInputs.splits,
        leaving: leaving,
      });
    } else {
      unseatPlayer(txnForm.tableId, txnForm.playerId);
      setTxnForm({ tableId: null, playerId: null });
      setTxnInputs({
        type: "deposit",
        credit: false,
        amount: "",
        method: "cash",
        settle: false,
        receiver: "",
        payLater: true,
        splits: [],
        leaving: false,
      });
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Debt Management
  // ============================================================================

  // 1) define a handler once in the component
  const handleAdditionalPayment = async (player) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await clearDebt(
        player.id,
        debtAmounts[player.id] || 0,
        methodAmounts[player.id] || "cash",
        true,
        clearReceivers[player.id] || "",
        txnForm.tableId,
      );

      // reset inner fields
      setDebtAmounts((prev) => ({ ...prev, [player.id]: "" }));
      setMethodAmounts((prev) => ({ ...prev, [player.id]: "" }));
      setClearReceivers((prev) => ({ ...prev, [player.id]: "" }));
    } catch (err) {
      console.error("Additional payment failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearDebt = async (
    playerId,
    amount,
    method,
    payingSessionCredit = false,
    walletId,
    tableId,
  ) => {
    try {
      await axios.post(`/${cardroom}/api/transactions/debt_payback`, {
        playerId,
        amount,
        method,
        payingSessionCredit,
        walletId,
        tableId,
      });

      const { data: allTables } = await axios.get(`/${cardroom}/api/tables`);
      const mapping = {};

      await Promise.all(
        allTables.map(async (tbl) => {
          try {
            const { data } = await axios.get(
              `/${cardroom}/api/tables/${tbl.id}/players`,
            );
            mapping[tbl.id] = data;
          } catch {
            mapping[tbl.id] = [];
          }
        }),
      );

      setTablePlayers(mapping);

      const { data: active } = await axios.get(
        `/${cardroom}/api/players/active`,
      );
      setActivePlayers(active);

      if (selectedPlayerId != null) {
        listPlayerTransactions(playerId);
      }
    } catch (err) {
      console.error("Clearing debt failed", err);
      throw err; // Re-throw so the form handler can catch it
    }
  };

  // ============================================================================
  // HANDLERS: Rake & Tips
  // ============================================================================

  const handleComboSubmit = async (e, tableId) => {
    e.preventDefault();
    if (isSubmitting) return;

    const submitter = e.nativeEvent?.submitter?.value;
    const form = comboForm[tableId] || {};
    const dealerId = form.dealerId;

    try {
      setIsSubmitting(true);

      if (submitter === "tip") {
        const amount = parseFloat(form.tip);
        if (isNaN(amount) || !dealerId) return;

        await axios.post(`/${cardroom}/api/tables/${tableId}/dealers_tips`, {
          dealerId: parseInt(dealerId, 10),
          amount,
        });

        setComboForm((prev) => ({
          ...prev,
          [tableId]: { ...(prev[tableId] ?? {}), tip: "" },
        }));

        if (selectedTableId != null) listTableTransactions(tableId);
        if (selectedDealerId != null) {
          const { data } = await axios.get(
            `/${cardroom}/api/dealers/${dealerId}/transactions`,
          );
          setSelectedDealerTransactions(data);
        }

        const { data: activeDlrs } = await axios.get(
          `/${cardroom}/api/dealers/active`,
        );
        setActiveDealers(activeDlrs);

        // Auto-update local state
        setDealers((prev) =>
          prev.map((d) =>
            Number(d.id) === Number(dealerId)
              ? {
                  ...d,
                  session_tips: (Number(d.session_tips) || 0) + amount,
                  owed: (Number(d.owed) || 0) + amount,
                }
              : d,
          ),
        );
      }

      if (submitter === "rake") {
        const amount = parseFloat(form.rake);
        if (isNaN(amount) || !dealerId) return;

        await axios.post(`/${cardroom}/api/tables/${tableId}/rake`, {
          amount,
          dealerId: parseInt(dealerId, 10),
        });

        setComboForm((prev) => ({
          ...prev,
          [tableId]: { ...(prev[tableId] ?? {}), rake: "" },
        }));

        if (selectedTableId != null) listTableTransactions(tableId);
      }

      if (submitter === "badBeat") {
        const amount = parseFloat(form.badBeat);
        if (isNaN(amount) || !dealerId) return;

        await axios.post(`/${cardroom}/api/tables/${tableId}/badbeat`, {
          amount,
          dealerId: parseInt(dealerId, 10),
        });

        setComboForm((prev) => ({
          ...prev,
          [tableId]: { ...(prev[tableId] ?? {}), badBeat: "" },
        }));
      }
    } catch (err) {
      console.error("Submit combo failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // HANDLERS: Transaction History
  // ============================================================================

  const listPlayerTransactions = async (playerId) => {
    setSelectedPlayerId(playerId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/players/${playerId}/transactions`,
      );
      setSelectedPlayerTransactions(data);
    } catch (err) {
      console.error("List player transactions failed", err);
    }
  };

  const listTableTransactions = async (tableId) => {
    setSelectedTableId(tableId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/transactions`,
      );
      setSelectedTableTransactions(data);
    } catch (err) {
      console.error("List table transactions failed", err);
    }
  };

  const listDealerTransactions = async (dealerId) => {
    setSelectedDealerId(dealerId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/dealers/${dealerId}/transactions`,
      );
      setSelectedDealerTransactions(data);
    } catch (err) {
      console.error("List dealer transactions failed", err);
    }
  };

  // IMPROVED FRONTEND DELETE TRANSACTION FUNCTION
  // Replace the existing deleteTransaction function in Game.jsx with this version

  const deleteTransaction = async (
    transactionId,
    code,
    playerId,
    amount,
    table_id,
    receiver_name = null,
  ) => {
    setIsSubmitting(true);

    try {
      // Find wallet if receiver_name is passed
      let computedWalletId = null;
      if (receiver_name) {
        const w = wallets.find((w) => w.name === receiver_name);
        if (w) computedWalletId = w.id;
      }

      await axios.delete(`/${cardroom}/api/deleteTransaction`, {
        data: {
          id: transactionId,
          code: code,
          player_id: playerId,
          amount: amount,
          table_id: table_id,
          wallet_id: computedWalletId,
        },
      });

      // Remove from UI transaction lists
      setSelectedPlayerTransactions((prev) =>
        prev.filter((tx) => tx.id !== transactionId),
      );
      setSelectedTableTransactions((prev) =>
        prev.filter((tx) => tx.id !== transactionId),
      );
      setSelectedDealerTransactions((prev) =>
        prev.filter((tx) => tx.id !== transactionId),
      );

      // Refresh active players (in case debt/chips changed)
      const { data: active } = await axios.get(
        `/${cardroom}/api/players/active`,
      );
      setActivePlayers(active);

      // If transaction affected a table, refresh that table's players
      if (table_id != null) {
        const { data: tablePlayersData } = await axios.get(
          `/${cardroom}/api/tables/${table_id}/players`,
        );
        setTablePlayers((prev) => ({ ...prev, [table_id]: tablePlayersData }));
      }

      // If this was a dealer tip transaction, refresh active dealers
      if (code === 22) {
        const { data: activeDlrs } = await axios.get(
          `/${cardroom}/api/dealers/active`,
        );
        setActiveDealers(activeDlrs);
      }

      // Refresh wallets if the transaction involved digital wallets
      if (computedWalletId) {
        const { data: allWallets } = await axios.get(
          `/${cardroom}/api/wallets`,
        );
        setWallets(allWallets);
      }

      // Show success notification (if you have a notification system)
      // addNotification(`${typeDisplay} transaction deleted successfully`, "success");
    } catch (err) {
      console.error("Error deleting transaction:", err);
      const errorMsg =
        err.response?.data?.error || "Failed to delete transaction";
      alert(errorMsg); // Replace with your notification system
      // addNotification(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // COMPUTED VALUES: Transaction Modal
  // ============================================================================

  const isTxnModalOpen = txnForm.tableId !== null && txnForm.playerId !== null;

  const modalPlayers = isTxnModalOpen
    ? tablePlayers[txnForm.tableId] || []
    : [];
  const modalPlayer =
    modalPlayers.find((pl) => pl.id === txnForm.playerId) || null;
  const modalOwed = Number(modalPlayer?.session_credit ?? 0);
  const modalRunning = Number(modalPlayer?.book ?? 0);

  const hasPlayerNotes = (player) => Boolean(player?.notes?.trim());
  const showPlayerNotes = (player) => {
    if (hasPlayerNotes(player)) {
      setNotesPlayer(player);
    }
  };
  const notesButton = (player, extraClass = "") =>
    hasPlayerNotes(player) ? (
      <button
        type="button"
        className={`game-icon-btn game-icon-btn--warning ${extraClass}`.trim()}
        onClick={() => showPlayerNotes(player)}
        title="Player Notes"
      >
        <FaExclamationTriangle />
      </button>
    ) : null;

  const selectedTable = tables.find((t) => t.id === selectedTableId);

  const handleTutorialConfirm = async () => {
    try {
      await axios.post(`/${cardroom}/api/cardroom/tutorial-seen`);
    } catch (markerErr) {
      console.error("Failed to mark tutorial as seen", markerErr);
    } finally {
      setShowTutorialModal(false);
    }
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="game">
      <GameSessionHeader
        activePage="game"
        cardroom={cardroom}
        currSession={currSession}
        dealerMode={dealerMode}
        permission={permission}
        title="Game Dashboard"
      />

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <FaBell className="game-notification__icon" />
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() =>
                  setNotifications((prev) => prev.filter((x) => x.id !== n.id))
                }
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      {showTutorialModal && (
        <GameTutorialModal
          onClose={() => setShowTutorialModal(false)}
          onConfirm={handleTutorialConfirm}
        />
      )}

      <div className="game-container">
        {loading ? (
          <div
            className="loading-container"
            style={{ padding: "4rem", textAlign: "center", color: "white" }}
          >
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
              Gathering Data...
            </h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="spinner"
                style={{
                  border: "4px solid rgba(255,255,255,0.1)",
                  borderTop: "4px solid #6366f1",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Add Table Section */}
            {/* Add Table Section */}
            {/* Pending Invites Section */}
            {pendingRequests.length > 0 && (
              <section
                className="game-section"
                style={{
                  border: "2px solid var(--game-primary)",
                  animation: "pulse-border 2s infinite",
                }}
              >
                <div
                  className="game-section__header"
                  onClick={() =>
                    setPendingRequestsCollapsed(!pendingRequestsCollapsed)
                  }
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <h2 className="game-section__title" style={{ margin: 0 }}>
                    Pending Waitlist Requests ({pendingRequests.length})
                  </h2>
                  {pendingRequestsCollapsed ? (
                    <FaChevronDown />
                  ) : (
                    <FaChevronUp />
                  )}
                </div>

                {!pendingRequestsCollapsed && (
                  <div className="pending-invites-grid">
                    {pendingRequests.map((req) => (
                      <div key={req.id} className="pending-invite-card">
                        <div className="pending-invite-info">
                          <span className="pending-invite-name">
                            {req.linked_player_id ? (
                              <span style={{ color: "var(--game-primary)" }}>
                                {req.nickname}
                              </span>
                            ) : (
                              `${req.first_name} ${req.last_name}`
                            )}
                          </span>
                          <span className="pending-invite-table-summary">
                            {req.rsvp === 1 ? (
                              <span
                                className="game-badge game-badge--info"
                                style={{
                                  marginRight: "8px",
                                  fontSize: "0.7rem",
                                }}
                              >
                                RSVP
                              </span>
                            ) : (
                              <span
                                className="game-badge game-badge--secondary"
                                style={{
                                  marginRight: "8px",
                                  fontSize: "0.7rem",
                                }}
                              >
                                Waitlist
                              </span>
                            )}
                            {req.table_name ? <b>{req.table_name} - </b> : ""}
                            {req.game_type} {req.stakes}
                          </span>
                        </div>
                        <div className="pending-invite-actions">
                          {req.linked_player_id ? (
                            <>
                              <button
                                className="game-btn game-btn--success"
                                onClick={() => approveRequest(req.id)}
                              >
                                Approve
                              </button>
                              <button
                                className="game-icon-btn game-icon-btn--danger"
                                onClick={() => rejectRequest(req.id)}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="game-icon-btn game-icon-btn--info"
                                onClick={() => {
                                  setLinkingRequest(req);
                                  setSelectedExistingPlayerId("");
                                }}
                              >
                                Link Existing
                              </button>
                              <button
                                className="game-icon-btn game-icon-btn--info"
                                onClick={() => {
                                  setLinkingNewRequest(req);
                                  setNewNickname(
                                    getDefaultPlayerNickname(
                                      req.first_name,
                                      req.last_name,
                                    ),
                                  );
                                }}
                              >
                                Link New
                              </button>
                              <button
                                className="game-icon-btn game-icon-btn--danger"
                                onClick={() => rejectRequest(req.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Add Table Section */}
            <section className="game-section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h2 className="game-section__title" style={{ margin: 0 }}>
                  Add New Table
                </h2>
                <div className="game-section-actions">
                  <button
                    className="game-btn game-btn--secondary game-btn--sm"
                    onClick={() => setShowPhoneLookupModal(true)}
                  >
                    Phone Lookup
                  </button>
                  <button
                    className="game-btn game-btn--secondary game-btn--sm"
                    onClick={() => setShowCodeLookupModal(true)}
                  >
                    Code Lookup
                  </button>
                  <button
                    className="game-btn game-btn--secondary game-btn--sm"
                    onClick={() => setShowQuickAddModal(true)}
                  >
                    Quick Add Player
                  </button>
                </div>
              </div>

              <form
                className="game-form"
                onSubmit={addTable}
                style={{ gap: "1rem" }}
              >
                <div
                  className="game-form-row"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "center",
                  }}
                >
                  <input
                    className="game-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Name"
                    style={{ flex: 1, minWidth: "150px" }}
                    type="text"
                  />

                  <select
                    className="game-input"
                    value={gameType}
                    onChange={(e) => setGameType(e.target.value)}
                    style={{ width: "auto" }}
                    required
                  >
                    <option value="" disabled>
                      Game Type
                    </option>
                    <option value="NLH">NLH</option>
                    <option value="PLO">PLO</option>
                    <option value="ROE">ROE</option>
                  </select>

                  <select
                    className="game-input"
                    value={stakes}
                    onChange={(e) => setStakes(e.target.value)}
                    style={{ width: "auto" }}
                    required
                  >
                    <option value="" disabled>
                      Stakes
                    </option>
                    <option value="1/3">1/3</option>
                    <option value="1/3/5">1/3/5</option>
                    <option value="5/5">5/5</option>
                    <option value="5/10">5/10</option>
                  </select>

                  <label className="game-checkbox" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={bombs}
                      onChange={(e) => setBombs(e.target.checked)}
                    />
                    <span>Bomb Pots</span>
                  </label>

                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={!gameType || !stakes || isSubmitting}
                  >
                    Create Table
                  </button>

                  {!showAdvanced && (
                    <button
                      type="button"
                      className="game-btn game-btn--ghost game-btn--sm"
                      onClick={() => setShowAdvanced(true)}
                    >
                      Show Advanced
                    </button>
                  )}
                </div>

                {showAdvanced && (
                  <div
                    className="game-form-row"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "1rem",
                      alignItems: "center",
                      paddingTop: "0.5rem",
                      borderTop: "1px solid var(--game-border)",
                    }}
                  >
                    <select
                      className="game-input"
                      value={maxPlayers || defaultMaxPlayers || 10}
                      onChange={(e) => setMaxPlayers(e.target.value)}
                      style={{ width: "auto" }}
                      title="Max Players"
                    >
                      {[11, 10, 9, 8, 7, 6].map((num) => (
                        <option key={num} value={num}>
                          {num} Max
                        </option>
                      ))}
                    </select>

                    <select
                      className="game-input"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      style={{ width: "auto" }}
                      title="Scheduled Start"
                    >
                      <option value="">No Start Time</option>
                      {(() => {
                        const times = [];
                        for (let h = 0; h < 24; h++) {
                          for (let m = 0; m < 60; m += 30) {
                            const hour = h % 12 || 12;
                            const ampm = h < 12 ? "AM" : "PM";
                            const mm = m === 0 ? "00" : m;
                            const label = `${hour}:${mm} ${ampm}`;
                            times.push(
                              <option key={label} value={label}>
                                {label}
                              </option>,
                            );
                          }
                        }
                        return times;
                      })()}
                    </select>

                    <label className="game-checkbox" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                      />
                      <span>Public</span>
                    </label>

                    <label className="game-checkbox" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={!isOpen}
                        onChange={(e) => setIsOpen(!e.target.checked)}
                      />
                      <span>Request Only</span>
                    </label>

                    {isPublic && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginLeft: "auto",
                        }}
                      >
                        <span
                          style={{
                            color: "#94a3b8",
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Registration Time
                        </span>
                        <input
                          type="datetime-local"
                          className="game-input"
                          value={registrationTime}
                          onChange={(e) => setRegistrationTime(e.target.value)}
                          title="Table visible to mobile users after this time"
                          style={{
                            width: "auto",
                            padding: "0.5rem",
                            borderRadius: "0.5rem",
                            border: "1px solid #334155",
                            background: "#1e293b",
                            color: "white",
                            fontSize: "0.875rem",
                          }}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      className="game-btn game-btn--ghost game-btn--sm"
                      style={{ marginLeft: isPublic ? undefined : "auto" }}
                      onClick={() => setShowAdvanced(false)}
                    >
                      Hide Advanced
                    </button>
                  </div>
                )}
              </form>
            </section>

            {/* Tables List */}
            <section className="game-section">
              <h2 className="game-section__title">
                Active Tables ({tables.length})
              </h2>

              <div className="game-tables">
                {(() => {
                  const allSeatedIds = Object.values(tablePlayers).reduce(
                    (set, arr) => {
                      arr.forEach((p) => set.add(p.id));
                      return set;
                    },
                    new Set(),
                  );

                  return tables.map((tbl) => {
                    const playersHere = tablePlayers[tbl.id] || [];
                    const waiting = tableWaitlists[tbl.id] || [];
                    const rsvpsHere = tableRsvps[tbl.id] || [];
                    const hasPlayers = playersHere.length > 0;

                    const options = players.map((p) => ({
                      value: p.id,
                      label: p.name,
                      fullName: getPlayerFullName(p),
                      searchText: getPlayerSearchText(p),
                      isSeated: allSeatedIds.has(p.id),
                    }));

                    const dealerOptions = dealers.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }));

                    const selectedId = selectedPlayerToAddId[tbl.id] || "";

                    return (
                      <div
                        key={tbl.id}
                        className={`game-table ${
                          openTables[tbl.id] ? "game-table--open" : ""
                        }`}
                      >
                        {/* Table Header */}
                        <div
                          className="game-table__header"
                          onClick={() =>
                            setOpenTables((prev) => ({
                              ...prev,
                              [tbl.id]: !prev[tbl.id],
                            }))
                          }
                        >
                          <div className="game-table__info">
                            <h3 className="game-table__name">
                              {tbl.name ? `${tbl.name} - ` : ""}
                              {tbl.stakes} {tbl.game_type}
                              {tbl.bomb_pots === 1 && (
                                <span
                                  className="game-table__bomb"
                                  style={{ marginLeft: "4px" }}
                                >
                                  💣
                                </span>
                              )}
                              {tbl.public === 0 && (
                                <span
                                  className="game-player-badge game-player-badge--debt"
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "10px",
                                  }}
                                >
                                  Private
                                </span>
                              )}
                              {tbl.open === 0 && (
                                <span
                                  className="game-player-badge game-player-badge--debt"
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "10px",
                                  }}
                                >
                                  Request Only
                                </span>
                              )}
                              {tbl.start_time === null &&
                                tbl.public === 1 &&
                                (!tbl.registration_time ||
                                  new Date(tbl.registration_time) <=
                                    new Date()) && (
                                  <span
                                    className="game-player-badge game-player-badge--running"
                                    style={{
                                      marginLeft: "8px",
                                      fontSize: "10px",
                                    }}
                                  >
                                    Registering
                                  </span>
                                )}
                            </h3>

                            <div className="game-table-meta-header">
                              <span className="game-table-meta-item">
                                {tbl.max_players}-Max
                              </span>
                              {(tbl.start_time || tbl.scheduled_at) && (
                                <span className="game-table-meta-item">
                                  {tbl.start_time
                                    ? new Date(
                                        tbl.start_time,
                                      ).toLocaleTimeString([], {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      })
                                    : tbl.scheduled_at}
                                </span>
                              )}
                            </div>

                            <span className="game-table__stats">
                              {playersHere.length === 0 &&
                              waiting.length === 0 &&
                              rsvpsHere.length === 0
                                ? "No players"
                                : `${playersHere.length} playing${waiting.length > 0 ? `, ${waiting.length} waiting` : ""}${rsvpsHere.length > 0 ? `, ${rsvpsHere.length} rsvp` : ""}`}
                            </span>
                          </div>

                          <div
                            className="game-table__actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className={`game-icon-btn ${
                                tbl.running
                                  ? "game-icon-btn--warning"
                                  : "game-icon-btn--success"
                              }`}
                              onClick={() =>
                                handleTableTime(tbl.id, tbl.running)
                              }
                              title={tbl.running ? "Pause" : "Start"}
                              disabled={!tbl.running && !currSession}
                            >
                              {tbl.running ? <FaPause /> : <FaPlay />}
                            </button>

                            <button
                              className="game-icon-btn game-icon-btn--info"
                              title="Table History"
                              onClick={() => {
                                listTableTransactions(tbl.id);
                                setTxFilters({
                                  rake: true,
                                  tips: true,
                                  deposit: true,
                                  withdrawal: true,
                                  book: true,
                                });
                              }}
                            >
                              <FaInfo />
                            </button>

                            <button
                              className="game-icon-btn game-icon-btn--danger"
                              disabled={
                                hasPlayers || waiting.length > 0 || tbl.running
                              }
                              onClick={() => {
                                if (hasPlayers || waiting.length > 0) {
                                  alert("Cannot delete a table with players");
                                } else {
                                  handleDeleteTable(tbl.id);
                                }
                              }}
                              title="Delete Table"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {openTables[tbl.id] && (
                          <div className="game-table__content">
                            {/* Player Controls */}
                            <div className="game-controls">
                              <form
                                className="game-controls__row"
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const pid = +selectedId;
                                  const action = e.nativeEvent.submitter.name;
                                  if (!pid) return;
                                  action === "seat"
                                    ? tbl.start_time
                                      ? seatPlayer(tbl.id, pid)
                                      : rsvpPlayer(tbl.id, pid)
                                    : addToWaitlist(tbl.id, pid);
                                  setSelectedPlayerToAddId((prev) => ({
                                    ...prev,
                                    [tbl.id]: "",
                                  }));
                                }}
                              >
                                <Select
                                  required
                                  options={options}
                                  isSearchable
                                  filterOption={filterPlayerOption}
                                  formatOptionLabel={formatPlayerOptionLabel}
                                  placeholder="Select Player"
                                  className="game-select"
                                  classNamePrefix="select"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      backgroundColor: "#2d3748",
                                      borderColor: "#4a5568",
                                      minWidth: "0",
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      backgroundColor: "#2d3748",
                                    }),
                                    option: (base, { data, isFocused }) => ({
                                      ...base,
                                      backgroundColor: isFocused
                                        ? "#4a5568"
                                        : "#2d3748",
                                      color: data.isSeated
                                        ? "#a0aec0"
                                        : "#e2e8f0",
                                      fontStyle: data.isSeated
                                        ? "italic"
                                        : "normal",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      color: "#e2e8f0",
                                    }),
                                    input: (base) => ({
                                      ...base,
                                      color: "#e2e8f0",
                                    }),
                                  }}
                                  onChange={(opt) =>
                                    setSelectedPlayerToAddId((prev) => ({
                                      ...prev,
                                      [tbl.id]: opt ? opt.value : "",
                                    }))
                                  }
                                  value={
                                    options.find(
                                      (o) => o.value === selectedId,
                                    ) || null
                                  }
                                />

                                {selectedPlayerToAddId[tbl.id] && (
                                  <>
                                    <button
                                      type="button"
                                      className="game-icon-btn"
                                      onClick={() =>
                                        setSelectedPlayerToAddId((prev) => ({
                                          ...prev,
                                          [tbl.id]: "",
                                        }))
                                      }
                                    >
                                      <FaTimes />
                                    </button>
                                    <button
                                      type="submit"
                                      name="seat"
                                      className="game-btn game-btn--primary"
                                      disabled={
                                        allSeatedIds.has(+selectedId) ||
                                        !selectedPlayerToAddId[tbl.id] ||
                                        (tbl.start_time && !currSession)
                                      }
                                      title={
                                        tbl.start_time === null
                                          ? "RSVP Player"
                                          : "Seat Player"
                                      }
                                    >
                                      <FaChair />{" "}
                                      {tbl.start_time === null
                                        ? "RSVP"
                                        : "Seat"}
                                    </button>

                                    <button
                                      type="submit"
                                      name="waitlist"
                                      className="game-btn game-btn--secondary"
                                      disabled={
                                        tablePlayers[tbl.id]?.includes(
                                          +selectedId,
                                        ) || !selectedPlayerToAddId[tbl.id]
                                      }
                                      title="Add to Waitlist"
                                    >
                                      <FaHourglassHalf /> Waitlist
                                    </button>
                                  </>
                                )}

                                {!selectedPlayerToAddId[tbl.id] &&
                                  tbl.start_time === null && (
                                    <span
                                      className="game-player-badge game-player-badge--running"
                                      style={{
                                        marginLeft: "8px",
                                        fontSize: "10px",
                                      }}
                                    >
                                      Start table to record transactions
                                    </span>
                                  )}

                                <div className="game-table-inline-settings">
                                  <select
                                    className="game-input game-input--small"
                                    value={tbl.max_players}
                                    onChange={(e) =>
                                      updateTableSettings(tbl.id, {
                                        maxPlayers: e.target.value,
                                        scheduledAt: tbl.scheduled_at,
                                        public: tbl.public,
                                        registrationTime: tbl.registration_time,
                                        open: tbl.open,
                                      })
                                    }
                                    title="Max Players"
                                  >
                                    {[11, 10, 9, 8, 7, 6].map((num) => (
                                      <option key={num} value={num}>
                                        {num} Max
                                      </option>
                                    ))}
                                  </select>

                                  {tbl.start_time === null && (
                                    <select
                                      className="game-input game-input--small"
                                      value={tbl.scheduled_at || ""}
                                      onChange={(e) =>
                                        updateTableSettings(tbl.id, {
                                          maxPlayers: tbl.max_players,
                                          scheduledAt: e.target.value,
                                          public: tbl.public,
                                          registrationTime:
                                            tbl.registration_time,
                                          open: tbl.open,
                                        })
                                      }
                                      title="Expected Start"
                                    >
                                      <option value="">No Time</option>
                                      {(() => {
                                        const times = [];
                                        for (let h = 0; h < 24; h++) {
                                          for (let m = 0; m < 60; m += 30) {
                                            const hour = h % 12 || 12;
                                            const ampm = h < 12 ? "AM" : "PM";
                                            const mm = m === 0 ? "00" : m;
                                            const label = `${hour}:${mm} ${ampm}`;
                                            times.push(
                                              <option key={label} value={label}>
                                                {label}
                                              </option>,
                                            );
                                          }
                                        }
                                        return times;
                                      })()}
                                    </select>
                                  )}

                                  <label
                                    className="game-checkbox"
                                    style={{ margin: 0, fontSize: "0.75rem" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={tbl.public === 1}
                                      onChange={(e) =>
                                        updateTableSettings(tbl.id, {
                                          maxPlayers: tbl.max_players,
                                          scheduledAt: tbl.scheduled_at,
                                          public: e.target.checked ? 1 : 0,
                                          registrationTime: e.target.checked
                                            ? tbl.registration_time
                                            : null,
                                          open: tbl.open,
                                        })
                                      }
                                    />
                                    <span>Public</span>
                                  </label>

                                  <label
                                    className="game-checkbox"
                                    style={{ margin: 0, fontSize: "0.75rem" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={tbl.open === 0}
                                      onChange={(e) =>
                                        updateTableSettings(tbl.id, {
                                          maxPlayers: tbl.max_players,
                                          scheduledAt: tbl.scheduled_at,
                                          public: tbl.public,
                                          registrationTime:
                                            tbl.registration_time,
                                          open: e.target.checked ? 0 : 1,
                                        })
                                      }
                                    />
                                    <span>Request Only</span>
                                  </label>

                                  {tbl.registration_time &&
                                    new Date(tbl.registration_time) >
                                      new Date() && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.4rem",
                                        }}
                                      >
                                        <span
                                          style={{
                                            color: "#94a3b8",
                                            fontSize: "0.7rem",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          Reg. Time
                                        </span>
                                        <input
                                          type="datetime-local"
                                          className="game-input game-input--small"
                                          value={
                                            tbl.registration_time
                                              ? tbl.registration_time.slice(
                                                  0,
                                                  16,
                                                )
                                              : ""
                                          }
                                          onChange={(e) =>
                                            updateTableSettings(tbl.id, {
                                              maxPlayers: tbl.max_players,
                                              scheduledAt: tbl.scheduled_at,
                                              public: tbl.public,
                                              registrationTime:
                                                e.target.value || null,
                                              open: tbl.open,
                                            })
                                          }
                                          title="Registration Time"
                                          style={{
                                            padding: "0.3rem",
                                            borderRadius: "0.5rem",
                                            border: "1px solid #334155",
                                            background: "#1e293b",
                                            color: "white",
                                            fontSize: "0.75rem",
                                          }}
                                        />
                                      </div>
                                    )}
                                </div>
                              </form>

                              {/* Dealer Controls */}
                              <form
                                className="game-controls__row game-controls__dealer"
                                onSubmit={(e) => handleComboSubmit(e, tbl.id)}
                              >
                                <Select
                                  required
                                  options={dealerOptions}
                                  isSearchable
                                  placeholder="Select Dealer"
                                  className="game-select"
                                  classNamePrefix="select"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      backgroundColor: "#2d3748",
                                      borderColor: "#4a5568",
                                      minWidth: "0",
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      backgroundColor: "#2d3748",
                                    }),
                                    option: (base, { isFocused }) => ({
                                      ...base,
                                      backgroundColor: isFocused
                                        ? "#4a5568"
                                        : "#2d3748",
                                      color: "#e2e8f0",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      color: "#e2e8f0",
                                    }),
                                    input: (base) => ({
                                      ...base,
                                      color: "#e2e8f0",
                                    }),
                                  }}
                                  value={
                                    dealerOptions.find(
                                      (o) =>
                                        o.value ===
                                        (comboForm[tbl.id]?.dealerId ?? ""),
                                    ) || null
                                  }
                                  onChange={(opt) =>
                                    setComboForm((prev) => ({
                                      ...prev,
                                      [tbl.id]: {
                                        dealerId: opt ? opt.value : "",
                                        rake: prev[tbl.id]?.rake ?? "",
                                        tip: prev[tbl.id]?.tip ?? "",
                                      },
                                    }))
                                  }
                                />
                                {comboForm[tbl.id]?.dealerId && (
                                  <>
                                    <button
                                      type="button"
                                      className="game-icon-btn"
                                      onClick={() =>
                                        setComboForm((prev) => ({
                                          ...prev,
                                          [tbl.id]: {
                                            ...(prev[tbl.id] ?? {}),
                                            dealerId: "",
                                          },
                                        }))
                                      }
                                    >
                                      <FaTimes />
                                    </button>

                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Rake"
                                      className="game-input game-input--small"
                                      value={comboForm[tbl.id]?.rake ?? ""}
                                      onWheel={preventModalNumberWheel}
                                      onChange={(e) =>
                                        setComboForm((prev) => ({
                                          ...prev,
                                          [tbl.id]: {
                                            ...(prev[tbl.id] ?? {}),
                                            rake: e.target.value,
                                          },
                                        }))
                                      }
                                    />
                                    <button
                                      type="submit"
                                      name="action"
                                      value="rake"
                                      className="game-btn game-btn--sm"
                                      disabled={
                                        tbl.start_time === null ||
                                        isSubmitting ||
                                        !comboForm[tbl.id]?.dealerId ||
                                        !comboForm[tbl.id]?.rake ||
                                        !currSession
                                      }
                                      title={
                                        tbl.start_time === null
                                          ? "Must start the table first"
                                          : "Submit Rake"
                                      }
                                    >
                                      <FaCheck />
                                    </button>

                                    <input
                                      type="number"
                                      step="1"
                                      min="0"
                                      placeholder="Tip"
                                      className="game-input game-input--small"
                                      value={comboForm[tbl.id]?.tip ?? ""}
                                      onWheel={preventModalNumberWheel}
                                      onChange={(e) =>
                                        setComboForm((prev) => ({
                                          ...prev,
                                          [tbl.id]: {
                                            ...(prev[tbl.id] ?? {}),
                                            tip: e.target.value,
                                          },
                                        }))
                                      }
                                    />
                                    <button
                                      type="submit"
                                      name="action"
                                      value="tip"
                                      className="game-btn game-btn--sm"
                                      disabled={
                                        tbl.start_time === null ||
                                        isSubmitting ||
                                        !comboForm[tbl.id]?.dealerId ||
                                        !comboForm[tbl.id]?.tip ||
                                        !currSession
                                      }
                                      title={
                                        tbl.start_time === null
                                          ? "Must start the table first"
                                          : "Submit Tip"
                                      }
                                    >
                                      <FaCheck />
                                    </button>
                                    {badBeatEnabled && (
                                      <>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="Bad Beat"
                                          className="game-input game-input--small"
                                          value={
                                            comboForm[tbl.id]?.badBeat ?? ""
                                          }
                                          onWheel={preventModalNumberWheel}
                                          onChange={(e) =>
                                            setComboForm((prev) => ({
                                              ...prev,
                                              [tbl.id]: {
                                                ...(prev[tbl.id] ?? {}),
                                                badBeat: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                        <button
                                          type="submit"
                                          name="action"
                                          value="badBeat"
                                          className="game-btn game-btn--sm"
                                          disabled={
                                            tbl.start_time === null ||
                                            isSubmitting ||
                                            !comboForm[tbl.id]?.dealerId ||
                                            !comboForm[tbl.id]?.badBeat ||
                                            !currSession
                                          }
                                          title={
                                            tbl.start_time === null
                                              ? "Must start the table first"
                                              : "Submit Bad Beat"
                                          }
                                        >
                                          <FaCheck />
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </form>
                            </div>

                            {/* Players, RSVPs and Waitlist */}
                            {(() => {
                              const hasPlaying = playersHere.length > 0;
                              const hasRsvp = rsvpsHere.length > 0;
                              const hasWaiting = waiting.length > 0;
                              const sectionCount =
                                (hasPlaying ? 1 : 0) +
                                (hasRsvp ? 1 : 0) +
                                (hasWaiting ? 1 : 0);

                              if (sectionCount === 0) {
                                return (
                                  <div className="game-players-grid">
                                    <div className="game-empty-state">
                                      {currSession
                                        ? "Seat players, RSVP them, or add them to the waitlist so they show up here."
                                        : "Start a session on the Session page to seat players at this table."}
                                    </div>
                                  </div>
                                );
                              }

                              const PlayingSection = (
                                <div className="game-player-section">
                                  <h4 className="game-player-section__title">
                                    Playing ({playersHere.length})
                                  </h4>
                                  <ul className="game-player-list">
                                    {playersHere.map((p) => (
                                      <li
                                        key={p.id}
                                        className="game-player-item"
                                      >
                                        {tables.length > 1 && (
                                          <button
                                            className="game-icon-btn game-icon-btn--sm"
                                            onClick={() => {
                                              setMovingPlayer({
                                                playerId: p.id,
                                                fromTableId: tbl.id,
                                                name: p.name,
                                              });
                                              setTargetTableId(null);
                                            }}
                                            title="Move to Another Table"
                                          >
                                            <FaArrowCircleUp />
                                          </button>
                                        )}
                                        <span
                                          className="game-player-name"
                                          onClick={() =>
                                            listPlayerTransactions(p.id)
                                          }
                                        >
                                          {p.name}
                                        </span>
                                        {notesButton(p, "game-icon-btn--sm")}

                                        <div className="game-player-badges-container">
                                          {(p.session_credit != 0 ||
                                            p.credit_limit > 0) && (
                                            <span
                                              className={`game-player-badge game-player-badge--session-yellow`}
                                            >
                                              {p.credit_limit > 0 &&
                                                p.session_credit !== 0 && (
                                                  <span
                                                    style={{ fontWeight: 600 }}
                                                  >
                                                    {" "}
                                                    ${p.session_credit} / $
                                                    {p.credit_limit -
                                                      (Number(p.book) || 0)}
                                                    &nbsp;Session Credit
                                                  </span>
                                                )}
                                              {p.credit_limit > 0 &&
                                                p.session_credit === 0 && (
                                                  <span
                                                    style={{ fontWeight: 600 }}
                                                  >
                                                    {" "}
                                                    ${p.credit_limit}{" "}
                                                    &nbsp;Credit Limit
                                                  </span>
                                                )}
                                              {!p.credit_limit && (
                                                <span
                                                  style={{ fontWeight: 600 }}
                                                >
                                                  {" "}
                                                  ${p.session_credit}{" "}
                                                  &nbsp;Session Credit
                                                </span>
                                              )}
                                            </span>
                                          )}

                                          {p.book > 0 && (
                                            <span className="game-player-badge game-player-badge--debt">
                                              <b>${p.book}</b>&nbsp;Account Debt
                                            </span>
                                          )}

                                          {p.book < 0 && (
                                            <span className="game-player-badge game-player-badge--credit">
                                              <b>${Math.abs(p.book)}</b>
                                              &nbsp;Account Balance
                                            </span>
                                          )}
                                        </div>

                                        <div className="game-player-actions">
                                          <button
                                            className="game-icon-btn game-icon-btn--success"
                                            onClick={() => {
                                              setTxnForm({
                                                tableId: tbl.id,
                                                playerId: p.id,
                                                fromX: false,
                                              });
                                              setTxnInputs({
                                                type: "deposit",
                                                credit: false,
                                                amount: "",
                                                method: "cash",
                                                receiver: "",
                                                settle: false,
                                                payLater: true,
                                                splits: [],
                                                leaving: false,
                                              });
                                            }}
                                            title="Add Transaction"
                                          >
                                            <FaDollarSign />
                                          </button>

                                          <button
                                            className="game-icon-btn game-icon-btn--info"
                                            onClick={() =>
                                              listPlayerTransactions(p.id)
                                            }
                                            title="Player History"
                                          >
                                            <FaInfo />
                                          </button>

                                          <button
                                            className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                            onClick={() => {
                                              setTxnForm({
                                                tableId: tbl.id,
                                                playerId: p.id,
                                                fromX: true,
                                              });
                                              setTxnInputs({
                                                type: "withdrawal",
                                                credit: false,
                                                amount: "0",
                                                method: "cash",
                                                receiver: "",
                                                settle: true,
                                                payLater: false,
                                                splits: [],
                                                leaving: true,
                                              });
                                            }}
                                            title="Cash Out"
                                          >
                                            <FaTimes />
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );

                              const RsvpSection = (
                                <div className="game-player-section">
                                  <h4 className="game-player-section__title">
                                    Reserved ({rsvpsHere.length})
                                  </h4>
                                  <ul className="game-player-list">
                                    {rsvpsHere.map((p) => (
                                      <li
                                        key={p.id}
                                        className="game-player-item"
                                      >
                                        <span
                                          className="game-player-name"
                                          onClick={() =>
                                            listPlayerTransactions(p.id)
                                          }
                                        >
                                          {p.name}
                                        </span>
                                        {notesButton(p, "game-icon-btn--sm")}
                                        <div className="game-player-actions">
                                          {tbl.start_time !== null && (
                                            <>
                                              <button
                                                className="game-icon-btn game-icon-btn--success"
                                                onClick={() =>
                                                  seatPlayer(tbl.id, p.id)
                                                }
                                                title="Seat Player"
                                              >
                                                <FaChair />
                                              </button>
                                              <button
                                                className="game-icon-btn game-icon-btn--info"
                                                onClick={() =>
                                                  moveFromRsvpToWaitlist(
                                                    tbl.id,
                                                    p.id,
                                                  )
                                                }
                                                title="Move to Waitlist"
                                              >
                                                <FaHourglassHalf />
                                              </button>
                                            </>
                                          )}
                                          <button
                                            className="game-icon-btn game-icon-btn--danger"
                                            onClick={() =>
                                              removeFromRsvp(tbl.id, p.id)
                                            }
                                            title="Remove RSVP"
                                          >
                                            <FaTimes />
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );

                              const WaitingSection = (
                                <div className="game-player-section">
                                  <h4 className="game-player-section__title">
                                    Waiting ({waiting.length})
                                  </h4>
                                  <ul className="game-player-list">
                                    {waiting.map((p, idx) => {
                                      const isSeated = allSeatedIds.has(p.id);
                                      const canMoveUp = idx > 0;
                                      const canMoveDown =
                                        idx < waiting.length - 1;
                                      return (
                                        <li
                                          key={p.id}
                                          className="game-player-item"
                                        >
                                          <span
                                            className="game-player-name"
                                            onClick={() =>
                                              listPlayerTransactions(p.id)
                                            }
                                          >
                                            {p.name}
                                          </span>
                                          {notesButton(p, "game-icon-btn--sm")}
                                          <WaitlistTimer
                                            updatedAt={p.updated_at}
                                            durationMinutes={waitlistDuration}
                                          />

                                          <div className="game-player-actions">
                                            {canMoveUp && (
                                              <button
                                                className="game-icon-btn game-icon-btn--sm"
                                                onClick={() =>
                                                  moveUpInWaitlist(tbl.id, p.id)
                                                }
                                                title="Move Up"
                                              >
                                                <FaArrowUp />
                                              </button>
                                            )}

                                            {canMoveDown && (
                                              <button
                                                className="game-icon-btn game-icon-btn--sm"
                                                onClick={() =>
                                                  moveDownInWaitlist(
                                                    tbl.id,
                                                    p.id,
                                                  )
                                                }
                                                title="Move Down"
                                              >
                                                <FaArrowDown />
                                              </button>
                                            )}

                                            <button
                                              className="game-icon-btn game-icon-btn--sm game-icon-btn--success"
                                              onClick={() =>
                                                moveUpFromWaitlist(tbl.id, p.id)
                                              }
                                              disabled={
                                                isSeated || !currSession
                                              }
                                              title="Seat Player"
                                            >
                                              <FaChair />
                                            </button>

                                            <button
                                              className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                              onClick={() =>
                                                removeFromWaitlist(tbl.id, p.id)
                                              }
                                              title="Remove from Waitlist"
                                            >
                                              <FaTimes />
                                            </button>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );

                              return (
                                <div
                                  className={`game-players-grid ${sectionCount === 1 ? "game-players-grid--single-col" : ""}`}
                                >
                                  {hasPlaying ? (
                                    <>
                                      {PlayingSection}
                                      {(hasRsvp || hasWaiting) && (
                                        <div className="game-players-column">
                                          {hasRsvp && RsvpSection}
                                          {hasWaiting && WaitingSection}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {hasRsvp && RsvpSection}
                                      {hasWaiting && WaitingSection}
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </section>

            {/* Active Players & Dealers Sidebar */}
            <div className="game-sidebar-grid">
              <section className="game-section">
                <h2 className="game-section__title">
                  Players in Session ({activePlayers.length})
                </h2>
                {activePlayers.length === 0 ? (
                  <p className="game-empty">No players in session yet</p>
                ) : (
                  <ul className="game-list">
                    {activePlayers.map((p) => (
                      <li key={p.id} className="game-list-item">
                        <span
                          className="game-list-name"
                          onClick={() => listPlayerTransactions(p.id)}
                        >
                          {p.name}
                        </span>

                        <div className="game-player-badges-container">
                          {(p.session_credit !== 0 || p.credit_limit > 0) && (
                            <span
                              className={`game-player-badge game-player-badge--session-yellow`}
                            >
                              <b>${p.session_credit}</b>
                              {p.credit_limit > 0 && (
                                <span style={{ fontWeight: 600 }}>
                                  {" "}
                                  / ${p.credit_limit - (Number(p.book) || 0)}
                                </span>
                              )}
                              &nbsp;Session Credit
                            </span>
                          )}

                          {p.book > 0 && (
                            <span className="game-player-badge game-player-badge--debt">
                              <b>${p.book}</b>&nbsp;Account Debt
                            </span>
                          )}

                          {p.book < 0 && (
                            <span className="game-player-badge game-player-badge--credit">
                              <b>${Math.abs(p.book)}</b>&nbsp;Account Balance
                            </span>
                          )}
                        </div>
                        <div className="game-player-actions">
                          {(p.session_credit !== 0 || Number(p.book) !== 0) && (
                            <button
                              className="game-icon-btn game-icon-btn--success"
                              onClick={() => {
                                setClearingPlayer(p);
                                setDebtAmounts((prev) => ({
                                  ...prev,
                                  [p.id]: "",
                                }));
                                setMethodAmounts((prev) => ({
                                  ...prev,
                                  [p.id]: "cash",
                                }));
                                setClearReceivers((prev) => ({
                                  ...prev,
                                  [p.id]: "",
                                }));
                                // Auto-select book type when there's no session credit
                                setClearingDebtType(
                                  p.session_credit === 0 ? "book" : "session",
                                );
                              }}
                              title="Clear Session Credit"
                            >
                              <FaDollarSign />
                            </button>
                          )}
                          <button
                            className="game-icon-btn game-icon-btn--info"
                            onClick={() => listPlayerTransactions(p.id)}
                            title="Player History"
                          >
                            <FaInfo />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="game-section">
                <h2 className="game-section__title">
                  Dealers in Session ({activeDealers.length})
                </h2>
                {activeDealers.length === 0 ? (
                  <p className="game-empty">No dealers in session yet</p>
                ) : (
                  <ul className="game-list">
                    {activeDealers.map((ad) => {
                      const d =
                        dealers.find(
                          (dlr) => Number(dlr.id) === Number(ad.id),
                        ) || ad;
                      return (
                        <li key={d.id} className="game-list-item">
                          <span
                            className="game-list-name"
                            onClick={() => listDealerTransactions(d.id)}
                          >
                            {d.name}
                          </span>
                          {d.session_tips !== undefined && (
                            <span className="game-dealer-tips">
                              Tips: ${parseInt(d.session_tips, 10)}
                            </span>
                          )}

                          <div className="game-player-actions">
                            {d.owed > 0 && (
                              <span
                                className="game-player-badge game-player-badge--running"
                                style={{ marginRight: "0.25rem" }}
                              >
                                Owed ${d.owed}
                              </span>
                            )}
                            {d.owed > 0 && (
                              <button
                                className="game-icon-btn game-icon-btn--success"
                                onClick={() => {
                                  setPayingDealer(d);
                                  setPayAmount(String(d.owed || ""));
                                  setPayMethod("cash");
                                  setPayReceiver("");
                                  setPaySplits([]);
                                }}
                                title="Pay Dealer"
                              >
                                <FaDollarSign />
                              </button>
                            )}

                            <button
                              className="game-icon-btn game-icon-btn--info"
                              onClick={() => listDealerTransactions(d.id)}
                              title="Dealer History"
                            >
                              <FaInfo />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
      {/* ============================================================================ */}
      {/* MODALS */}
      {/* ============================================================================ */}

      {/* Move Player Modal */}
      {movingPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setMovingPlayer(null)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Table Change</h3>
            </div>
            <div className="game-modal__body">
              <label className="game-label">
                Move {movingPlayer.name} from{" "}
                {tables.find((t) => t.id === movingPlayer.fromTableId)?.name ||
                  "Table " + movingPlayer.fromTableId}{" "}
                to:
              </label>
              <select
                className="game-input"
                value={targetTableId ?? ""}
                onChange={(e) => setTargetTableId(Number(e.target.value))}
              >
                <option value="">Choose Table</option>
                {tables
                  .filter((t) => t.id !== movingPlayer.fromTableId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || `Table ${t.id}`}
                    </option>
                  ))}
              </select>
            </div>
            <div className="game-modal__footer">
              <button
                className="game-btn game-btn--ghost"
                onClick={() => setMovingPlayer(null)}
              >
                Cancel
              </button>
              <button
                className="game-btn game-btn--primary"
                disabled={!targetTableId}
                onClick={async () => {
                  if (!targetTableId) return;
                  await unseatPlayer(
                    movingPlayer.fromTableId,
                    movingPlayer.playerId,
                  );
                  await seatPlayer(targetTableId, movingPlayer.playerId, false);
                  setMovingPlayer(null);
                  setTargetTableId(null);
                }}
              >
                <FaCheck /> Move Player
              </button>
            </div>
          </div>
        </>
      )}

      {/* Clear Session Credit Modal */}
      {clearingPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setClearingPlayer(null)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Clear Balance for {clearingPlayer.name}
              </h3>
            </div>
            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await clearDebt(
                    clearingPlayer.id,
                    clearingDebtType === "book" && clearingPlayer.book < 0
                      ? -(debtAmounts[clearingPlayer.id] || 0)
                      : debtAmounts[clearingPlayer.id] || 0,
                    methodAmounts[clearingPlayer.id] || "cash",
                    clearingDebtType === "session",
                    clearReceivers[clearingPlayer.id] || "",
                  );
                  setDebtAmounts((prev) => ({
                    ...prev,
                    [clearingPlayer.id]: "",
                  }));
                  setMethodAmounts((prev) => ({
                    ...prev,
                    [clearingPlayer.id]: "",
                  }));
                  setClearReceivers((prev) => ({
                    ...prev,
                    [clearingPlayer.id]: "",
                  }));
                  setClearingPlayer(null);
                }}
              >
                {/* Type selection — radio-style checkboxes */}
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    marginBottom: "0.25rem",
                  }}
                >
                  {clearingPlayer.session_credit > 0 && (
                    <label
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.625rem",
                        padding: "0.75rem 1rem",
                        border: `2px solid ${clearingDebtType === "session" ? "#d69e2e" : "var(--game-border)"}`,
                        borderRadius: "var(--game-radius)",
                        cursor: "pointer",
                        background:
                          clearingDebtType === "session"
                            ? "rgba(214,158,46,0.1)"
                            : "var(--game-bg-tertiary)",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={clearingDebtType === "session"}
                        onChange={() => {
                          if (clearingDebtType !== "session") {
                            setClearingDebtType("session");
                            setDebtAmounts((p) => ({
                              ...p,
                              [clearingPlayer.id]: "",
                            }));
                            setMethodAmounts((p) => ({
                              ...p,
                              [clearingPlayer.id]: "cash",
                            }));
                            setClearReceivers((p) => ({
                              ...p,
                              [clearingPlayer.id]: "",
                            }));
                          }
                        }}
                        style={{
                          width: 18,
                          height: 18,
                          accentColor: "#d69e2e",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ fontWeight: 600, color: "#d69e2e" }}>
                        Session Credit
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontWeight: 700,
                          color: "#d69e2e",
                        }}
                      >
                        ${clearingPlayer.session_credit}
                      </span>
                    </label>
                  )}

                  {Number(clearingPlayer.book) !== 0 && (
                    <label
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.625rem",
                        padding: "0.75rem 1rem",
                        border: `2px solid ${
                          clearingDebtType === "book"
                            ? clearingPlayer.book < 0
                              ? "var(--game-success)"
                              : "var(--game-danger)"
                            : "var(--game-border)"
                        }`,
                        borderRadius: "var(--game-radius)",
                        cursor: "pointer",
                        background:
                          clearingDebtType === "book"
                            ? clearingPlayer.book < 0
                              ? "rgba(72,187,120,0.1)"
                              : "rgba(245,101,101,0.1)"
                            : "var(--game-bg-tertiary)",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={clearingDebtType === "book"}
                        onChange={() => {
                          if (clearingDebtType !== "book") {
                            setClearingDebtType("book");
                            setDebtAmounts((p) => ({
                              ...p,
                              [clearingPlayer.id]: "",
                            }));
                            setMethodAmounts((p) => ({
                              ...p,
                              [clearingPlayer.id]: "cash",
                            }));
                            setClearReceivers((p) => ({
                              ...p,
                              [clearingPlayer.id]: "",
                            }));
                          }
                        }}
                        style={{
                          width: 18,
                          height: 18,
                          cursor: "pointer",
                          accentColor:
                            clearingPlayer.book < 0
                              ? "var(--game-success)"
                              : "var(--game-danger)",
                        }}
                      />
                      <span
                        style={{
                          fontWeight: 600,
                          color:
                            clearingPlayer.book < 0
                              ? "var(--game-success)"
                              : "var(--game-danger)",
                        }}
                      >
                        {clearingPlayer.book < 0
                          ? "Account Balance"
                          : "Account Debt"}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontWeight: 700,
                          color:
                            clearingPlayer.book < 0
                              ? "var(--game-success)"
                              : "var(--game-danger)",
                        }}
                      >
                        ${Math.abs(clearingPlayer.book)}
                      </span>
                    </label>
                  )}
                </div>

                {clearingDebtType === "book" && clearingPlayer.book < 0 && (
                  <p
                    style={{
                      fontSize: "0.8em",
                      color: "var(--game-text-secondary)",
                      margin: "0 0 0.25rem",
                    }}
                  >
                    House is paying out from player's account balance.
                  </p>
                )}
                {clearingDebtType === "book" && clearingPlayer.book > 0 && (
                  <p
                    style={{
                      fontSize: "0.8em",
                      color: "var(--game-text-secondary)",
                      margin: "0 0 0.25rem",
                    }}
                  >
                    Player is paying house to reduce their account debt.
                  </p>
                )}
                {clearingDebtType === "session" && (
                  <p
                    style={{
                      fontSize: "0.8em",
                      color: "var(--game-text-secondary)",
                      margin: "0 0 0.25rem",
                    }}
                  >
                    Player is paying house to reduce their session credit.
                  </p>
                )}
                <input
                  type="number"
                  className="game-input"
                  ref={clearingAmountInputRef}
                  value={debtAmounts[clearingPlayer.id] || ""}
                  placeholder={`Max ${clearingDebtType === "session" ? clearingPlayer.session_credit : Math.abs(Number(clearingPlayer.book))}`}
                  step="1"
                  max={
                    clearingDebtType === "session"
                      ? clearingPlayer.session_credit
                      : Math.abs(Number(clearingPlayer.book))
                  }
                  onChange={(e) =>
                    setDebtAmounts((prev) => ({
                      ...prev,
                      [clearingPlayer.id]: e.target.value,
                    }))
                  }
                  onWheel={preventModalNumberWheel}
                  required
                />

                <select
                  className="game-input"
                  value={methodAmounts[clearingPlayer.id] || "cash"}
                  onChange={(e) => {
                    setMethodAmounts((prev) => ({
                      ...prev,
                      [clearingPlayer.id]: e.target.value,
                    }));
                  }}
                  required
                >
                  <option value="" hidden>
                    Method
                  </option>
                  <option value="cash">Cash</option>
                  {(clearingDebtType === "session" ||
                    clearingPlayer.book > 0) && (
                    <option value="chips">Chips</option>
                  )}
                  {clearingDebtType !== "book" &&
                    debtAmounts[clearingPlayer.id] > clearingPlayer.book &&
                    clearingPlayer.book < 0 &&
                    debtAmounts[clearingPlayer.id] <=
                      clearingPlayer.book * -1 && (
                      <option value="book">Player Balance</option>
                    )}
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="zelle">Zelle</option>
                  <option value="applePay">Apple Pay</option>
                  <option value="paypal">PayPal</option>
                </select>

                {!["cash", "chips", "book", ""].includes(
                  methodAmounts[clearingPlayer.id] ?? "cash",
                ) && (
                  <select
                    className="game-input"
                    value={clearReceivers[clearingPlayer.id] || ""}
                    onChange={(e) =>
                      setClearReceivers((prev) => ({
                        ...prev,
                        [clearingPlayer.id]: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="" hidden>
                      {clearingDebtType === "book" && clearingPlayer.book < 0
                        ? "From"
                        : "To"}
                    </option>
                    {wallets.length > 0 ? (
                      wallets.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>None</option>
                    )}
                  </select>
                )}

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setClearingPlayer(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      !debtAmounts[clearingPlayer.id] ||
                      Number(debtAmounts[clearingPlayer.id]) >
                        (clearingDebtType === "session"
                          ? clearingPlayer.session_credit
                          : Math.abs(Number(clearingPlayer.book))) ||
                      (!["cash", "chips", "book"].includes(
                        methodAmounts[clearingPlayer.id] || "cash",
                      ) &&
                        !clearReceivers[clearingPlayer.id])
                    }
                  >
                    Submit Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Player Transaction History Modal */}
      {selectedPlayerId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedPlayerId(null);
              setSelectedPlayerTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {players.find((p) => p.id === selectedPlayerId)?.name}'s
                Transactions
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedPlayerId(null);
                  setSelectedPlayerTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "book", label: "Book" },
                { key: "deposit", label: "Deposits" },
                { key: "withdrawal", label: "Withdrawals" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={playerTxFilters[key]}
                    onChange={(e) =>
                      setPlayerTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
              <div className="game-filter-actions">
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setPlayerTxFilters({
                      book: true,
                      deposit: true,
                      withdrawal: true,
                    })
                  }
                >
                  All
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setPlayerTxFilters({
                      book: false,
                      deposit: false,
                      withdrawal: false,
                    })
                  }
                >
                  None
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = selectedPlayerTransactions.filter((t) => {
                  if (t.method === "cash out") return false;

                  const isDeposit = DEPOSIT_CODES.includes(t.code);
                  const isWithdrawal = WITHDRAWAL_CODES.includes(t.code);
                  const isBook = BOOK_CODES.includes(t.code);

                  return (
                    (isDeposit && playerTxFilters.deposit) ||
                    (isWithdrawal && playerTxFilters.withdrawal) ||
                    (isBook && playerTxFilters.book)
                  );
                });

                if (filtered.length === 0)
                  return (
                    <p className="game-empty">No matching transactions.</p>
                  );

                return (
                  <div className="game-transaction-list">
                    {(filtered.some((t) => t.player_book !== 0) ||
                      filtered.some((t) => t.player_credit !== 0)) && (
                      <div
                        style={{
                          overflowX: "auto",
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          flexWrap: "wrap",
                          marginBottom: "10px",
                        }}
                      >
                        {filtered.some(
                          (t) =>
                            t.player_credit != 0 && t.player_credit != null,
                        ) && (
                          <span className="game-player-badge game-player-badge--session-yellow">
                            Session Credit
                          </span>
                        )}

                        {filtered.some((t) => t.player_book > 0) && (
                          <span className="game-player-badge game-player-badge--debt">
                            Account Debt
                          </span>
                        )}

                        {filtered.some((t) => t.player_book < 0) && (
                          <span className="game-player-badge game-player-badge--credit">
                            Player Balance
                          </span>
                        )}
                      </div>
                    )}
                    {filtered.map((t) => {
                      const amt = parseFloat(t.amount).toFixed(0);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {new Date(t.completed_at).toLocaleTimeString(
                              undefined,
                              {
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                          <span className="game-transaction-amount">
                            ${Math.abs(amt)}
                          </span>
                          <span className="game-transaction-type">
                            {formatTransactionCode(t.code, t.method)}
                          </span>
                          <span className="game-transaction-method">
                            {t.credit
                              ? "credit"
                              : t.method
                                ? ` ${t.method}${
                                    t.receiver_name
                                      ? (WITHDRAWAL_CODES.includes(t.code)
                                          ? " from "
                                          : " to ") + t.receiver_name
                                      : ""
                                  }`
                                : ""}
                          </span>
                          <div
                            className="game-player-badges-container"
                            style={{ display: "flex", gap: "4px" }}
                          >
                            {t.player_credit != 0 &&
                              t.player_credit != null && (
                                <span className="game-player-badge game-player-badge--session-yellow">
                                  ${t.player_credit}
                                </span>
                              )}
                            {t.player_book > 0 && (
                              <span className="game-player-badge game-player-badge--debt">
                                ${t.player_book}
                              </span>
                            )}
                            {t.player_book < 0 && (
                              <span className="game-player-badge game-player-badge--credit">
                                ${Math.abs(t.player_book)}
                              </span>
                            )}
                          </div>
                          <span className="game-transaction-table">
                            {t.table_id
                              ? t.name || `Table id: ${t.table_id}`
                              : null}
                          </span>
                          <span className="game-transaction-date">
                            {new Date(t.completed_at).toLocaleDateString(
                              undefined,
                              {
                                month: "numeric",
                                day: "numeric",
                              },
                            )}
                          </span>
                          {t.code !== 30 && t.code !== 31 && (
                            <button
                              className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                              disabled={!currSession}
                              title={
                                currSession
                                  ? ""
                                  : "Can only delete from active session"
                              }
                              onClick={() =>
                                deleteTransaction(
                                  t.id,
                                  t.code,
                                  selectedPlayerId,
                                  t.amount,
                                  t.table_id,
                                  t.receiver_name,
                                )
                              }
                            >
                              <FaRegTrashAlt />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Dealer Transaction History Modal */}
      {selectedDealerId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedDealerId(null);
              setSelectedDealerTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {dealers.find((d) => d.id === selectedDealerId)?.name}'s
                Transactions
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedDealerId(null);
                  setSelectedDealerTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "rake", label: "Rake" },
                { key: "tips", label: "Tips" },
                { key: "payout", label: "Payout" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={dealerTxFilters[key]}
                    onChange={(e) =>
                      setDealerTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
              <div className="game-filter-actions">
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setDealerTxFilters({
                      rake: true,
                      tips: true,
                      payout: true,
                    })
                  }
                >
                  All
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--sm"
                  onClick={() =>
                    setDealerTxFilters({
                      rake: false,
                      tips: false,
                      payout: false,
                    })
                  }
                >
                  None
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = selectedDealerTransactions.filter((t) => {
                  const code = Number(t.code);
                  const isRake = RAKE_CODES.includes(code);
                  const isTip = TIP_CODES.includes(code);
                  const isPayout = DEALER_PAYOUT_CODES.includes(code);

                  if (isRake && dealerTxFilters.rake) return true;
                  if (isTip && dealerTxFilters.tips) return true;
                  if (isPayout && dealerTxFilters.payout) return true;

                  // fallback for items without matching codes if any
                  if (!isRake && !isTip && !isPayout) return true;
                  return false;
                });

                if (filtered.length === 0) {
                  return (
                    <p className="game-empty">No matching transactions.</p>
                  );
                }

                return (
                  <div className="game-transaction-list">
                    {filtered.map((t) => {
                      const amt = parseFloat(t.amount).toFixed(0);
                      const codeName = formatTransactionCode(t.code);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {new Date(t.completed_at).toLocaleTimeString(
                              undefined,
                              {
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                          <span className="game-transaction-amount">
                            ${amt}
                          </span>
                          <strong className="game-transaction-player">
                            {codeName}
                          </strong>
                          <span className="game-transaction-method">
                            {t.method}
                          </span>
                          <span className="game-transaction-table">
                            {t.name
                              ? t.name
                              : t.table_id
                                ? `Table ID: ${t.table_id}`
                                : ""}
                          </span>
                          <span className="game-transaction-date">
                            {new Date(t.completed_at).toLocaleDateString(
                              undefined,
                              {
                                month: "numeric",
                                day: "numeric",
                              },
                            )}
                          </span>
                          <button
                            className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                            disabled={!currSession}
                            title={
                              currSession
                                ? ""
                                : "Can only delete from active session"
                            }
                            onClick={() =>
                              deleteTransaction(
                                t.id,
                                t.code,
                                selectedDealerId,
                                t.amount,
                                t.table_id,
                                t.receiver_name || t.wallet_name,
                              )
                            }
                          >
                            <FaRegTrashAlt />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Table Transaction History Modal */}
      {selectedTableId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedTableId(null);
              setSelectedTableTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Transactions for{" "}
                {selectedTable?.name ?? `Table id: ${selectedTableId}`}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedTableId(null);
                  setSelectedTableTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "rake", label: "Rake" },
                { key: "tips", label: "Tips" },
                { key: "deposit", label: "Deposits" },
                { key: "withdrawal", label: "Withdrawals" },
                { key: "book", label: "Books" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={txFilters[key]}
                    onChange={(e) =>
                      setTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = selectedTableTransactions.filter((t) => {
                  const code = t.code;

                  const isRake = code && RAKE_CODES.includes(code);
                  const isTip = code && TIP_CODES.includes(code);
                  const isDeposit = code && DEPOSIT_CODES.includes(code);
                  const isWithdrawal = code && WITHDRAWAL_CODES.includes(code);
                  const isBook = code && BOOK_CODES.includes(code);

                  let isMatched = false;
                  let show = false;

                  if (isRake) {
                    isMatched = true;
                    if (txFilters.rake) show = true;
                  }
                  if (isTip) {
                    isMatched = true;
                    if (txFilters.tips) show = true;
                  }
                  if (isDeposit) {
                    isMatched = true;
                    if (txFilters.deposit) show = true;
                  }
                  if (isWithdrawal) {
                    isMatched = true;
                    if (txFilters.withdrawal) show = true;
                  }
                  if (isBook) {
                    isMatched = true;
                    if (txFilters.book) show = true;
                  }

                  if (isMatched) return show;
                  return true;
                });

                if (filtered.length === 0)
                  return (
                    <p className="game-empty">No matching transactions.</p>
                  );

                return (
                  <div className="game-transaction-list">
                    {filtered.map((t) => {
                      const amt = parseFloat(t.amount).toFixed(0);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {new Date(t.completed_at).toLocaleTimeString(
                              undefined,
                              {
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                          <strong className="game-transaction-player">
                            {t.player_or_dealer || ""}
                          </strong>
                          <span className="game-transaction-amount">
                            ${Math.abs(amt)}
                          </span>
                          <span className="game-transaction-type">
                            {formatTransactionCode(t.code, t.method)}
                          </span>
                          <span className="game-transaction-method">
                            {t.credit
                              ? "credit"
                              : t.method
                                ? ` ${t.method}${
                                    t.receiver_name
                                      ? (WITHDRAWAL_CODES.includes(t.code)
                                          ? " from "
                                          : " to ") + t.receiver_name
                                      : ""
                                  }`
                                : ""}
                          </span>
                          <span className="game-transaction-date">
                            {new Date(t.completed_at).toLocaleDateString(
                              undefined,
                              {
                                month: "numeric",
                                day: "numeric",
                              },
                            )}
                          </span>
                          <button
                            className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                            disabled={!currSession}
                            title={
                              currSession
                                ? ""
                                : "Can only delete from active session"
                            }
                            onClick={() =>
                              deleteTransaction(
                                t.id,
                                t.code,
                                t.player_id,
                                t.amount,
                                selectedTableId,
                                t.receiver_name,
                              )
                            }
                          >
                            <FaRegTrashAlt />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {notesPlayer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setNotesPlayer(null)}
            style={{ zIndex: 4000 }}
          />
          <div className="game-modal" style={{ zIndex: 4001 }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Player Notes for {notesPlayer.name}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setNotesPlayer(null)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {notesPlayer.notes}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Transaction Modal*/}
      {isTxnModalOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setTxnForm({ tableId: null, playerId: null })}
          />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {modalPlayer ? modalPlayer.name : "Player"}
              </h3>
              <div className="game-modal__header-badges">
                {notesButton(modalPlayer)}

                {modalRunning < 0 && (
                  <span className="game-player-badge game-player-badge--credit">
                    <b>${Math.abs(modalRunning)}</b>&nbsp;Account Balance
                  </span>
                )}

                {modalRunning > 0 && (
                  <span className="game-player-badge game-player-badge--debt">
                    <b>${Math.abs(modalRunning)}</b>&nbsp;Account Debt
                  </span>
                )}

                {(Number(modalOwed) > 0 || modalPlayer?.credit_limit > 0) && (
                  <span className={`game-badge game-badge--session-yellow`}>
                    {modalPlayer?.credit_limit > 0 && modalOwed !== 0 && (
                      <span style={{ fontWeight: 600 }}>
                        {" "}
                        <b>${modalOwed}</b> / $
                        {modalPlayer.credit_limit -
                          (Number(modalPlayer.book) || 0)}
                        &nbsp;Session Credit
                      </span>
                    )}
                    {modalPlayer?.credit_limit > 0 && modalOwed === 0 && (
                      <span style={{ fontWeight: 600 }}>
                        {" "}
                        $
                        {modalPlayer.credit_limit -
                          (Number(modalPlayer.book) || 0)}{" "}
                        &nbsp;Credit Limit
                      </span>
                    )}
                    {!modalPlayer?.credit_limit && (
                      <span style={{ fontWeight: 600 }}>
                        {" "}
                        ${modalPlayer.session_credit} &nbsp;Session Credit
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            <div className="game-modal__body">
              {(() => {
                const isDeposit = txnInputs.type === "deposit";
                const isDebtPayback = txnInputs.type === "debt";
                const isWithdrawal = txnInputs.type === "withdrawal";
                const isAccountBalance = txnInputs.type === "account_balance";
                const owes = Number(modalOwed) > 0;
                const isBonus = txnInputs.type === "bonus";

                const amountNum = Number(txnInputs.amount || 0);

                let exceedLimit = false;
                let availableCredit = 0;
                if (
                  isDeposit &&
                  txnInputs.credit &&
                  modalPlayer?.credit_limit > 0
                ) {
                  availableCredit =
                    modalPlayer.credit_limit -
                    (Number(modalPlayer.book) || 0) -
                    modalOwed;
                  if (amountNum > availableCredit) {
                    exceedLimit = true;
                  }
                }

                const hasAmount = Number.isFinite(amountNum) && amountNum >= 0;

                const canSettle = isWithdrawal && owes;
                const isSettling = canSettle && !!txnInputs.settle;

                const showMethodSelect =
                  !isBonus &&
                  (isDebtPayback ||
                    isAccountBalance ||
                    (!txnInputs.credit &&
                      (isDeposit ||
                        (isWithdrawal &&
                          (!isSettling ||
                            !hasAmount ||
                            amountNum > modalOwed)))));

                const nonCashNonChips =
                  txnInputs.method &&
                  !["cash", "chips"].includes(txnInputs.method);

                const needReceiver =
                  txnInputs.method !== "mixed" &&
                  ((isDebtPayback && txnInputs.method !== "book") ||
                    (isAccountBalance && txnInputs.method !== "book") ||
                    (isDeposit &&
                      !txnInputs.credit &&
                      txnInputs.method !== "book") ||
                    (isWithdrawal &&
                      txnInputs.method !== "book" &&
                      (!isSettling || amountNum > modalOwed))) &&
                  nonCashNonChips;

                const canRollDebt =
                  isWithdrawal &&
                  isSettling &&
                  hasAmount &&
                  amountNum < modalOwed;

                const isMixed = isWithdrawal && txnInputs.method === "mixed";
                const needsWallet = (m) =>
                  m && !["cash", "book", "chips"].includes(m);

                const totalAmt = Number(txnInputs.amount || 0) || 0;
                const owedNum = Number(modalOwed) || 0;
                const chipsUsed =
                  isMixed && canSettle && !!txnInputs.settle
                    ? Math.min(totalAmt, owedNum)
                    : 0;
                const payoutTarget = Math.max(totalAmt - chipsUsed, 0);

                const splits = txnInputs.splits || [];
                const sumSplits = splits.reduce(
                  (s, r) => s + (Number(r.amount || 0) || 0),
                  0,
                );

                const rowsValid =
                  payoutTarget === 0 ||
                  (splits.length > 0 &&
                    splits.every(
                      (r) =>
                        Number(r.amount) > 0 &&
                        !!r.method &&
                        (needsWallet(r.method) ? !!r.receiver : true),
                    ));

                // Check for duplicate method+wallet combinations instead of just duplicate methods
                const methodWalletPairs = splits
                  .filter((r) => r.method)
                  .map((r) => {
                    // For methods that need wallets, create a unique key combining method + wallet
                    if (needsWallet(r.method)) {
                      return `${r.method}-${r.receiver || ""}`;
                    }
                    // For cash/book/chips, just use the method (these can't be duplicated)
                    return r.method;
                  });

                const hasDuplicateMethodWalletPairs =
                  new Set(methodWalletPairs).size !== methodWalletPairs.length;

                const mixedInvalid =
                  sumSplits !== payoutTarget ||
                  !rowsValid ||
                  hasDuplicateMethodWalletPairs;

                const submitDisabled =
                  !hasAmount ||
                  exceedLimit ||
                  (amountNum < 0 && isWithdrawal) ||
                  (amountNum <= 0 && !isWithdrawal && !isBonus) ||
                  (isDeposit && !txnInputs.credit && !txnInputs.method) ||
                  (isWithdrawal &&
                    !isSettling &&
                    !txnInputs.method &&
                    !txnInputs.amount) ||
                  (isDebtPayback && !txnInputs.method) ||
                  (isAccountBalance && !txnInputs.method) ||
                  (isAccountBalance && amountNum > Math.abs(modalRunning)) ||
                  (needReceiver && !txnInputs.receiver) ||
                  (isDebtPayback && txnInputs.amount > modalOwed) ||
                  (isWithdrawal &&
                    isSettling &&
                    amountNum < modalOwed &&
                    !txnInputs.payLater) ||
                  (isMixed && mixedInvalid);
                const updateType = (nextType) => {
                  setTxnInputs((i) => ({
                    ...i,
                    type: nextType,
                    credit: false,
                    settle: nextType === "withdrawal",
                    method: defaultTxnMethodFor(nextType),
                    receiver: "",
                  }));
                };

                const updateCredit = (checked) => {
                  setTxnInputs((i) => ({
                    ...i,
                    credit: checked,
                    method: defaultTxnMethodFor(i.type, checked),
                    receiver: "",
                  }));
                };

                const updateSettle = (checked) => {
                  setTxnInputs((i) => ({
                    ...i,
                    settle: checked,
                    method: checked ? defaultTxnMethodFor(i.type, i.credit) : "cash",
                    receiver: "",
                  }));
                };

                const updateAmount = (val) => {
                  const next = val.replace(/[^\d.]/g, "");
                  setTxnInputs((i) => {
                    return {
                      ...i,
                      amount: next,
                    };
                  });
                };

                const updateMethod = (val) => {
                  setTxnInputs((i) => ({
                    ...i,
                    method: val,
                    receiver: ["cash", "chips", "book", "", "mixed"].includes(
                      val,
                    )
                      ? ""
                      : i.receiver,
                    splits:
                      val === "mixed"
                        ? i.splits?.length
                          ? i.splits
                          : [{ amount: "", method: "cash", receiver: "" }]
                        : [],
                  }));
                };

                const updateReceiver = (val) => {
                  setTxnInputs((i) => ({ ...i, receiver: val }));
                };

                const updateSplit = (idx, field, value) => {
                  setTxnInputs((i) => {
                    const next = [...(i.splits || [])];
                    next[idx] = { ...next[idx], [field]: value };
                    if (field === "method" && !needsWallet(value))
                      next[idx].receiver = "";
                    return { ...i, splits: next };
                  });
                };

                const addSplit = () => {
                  setTxnInputs((i) => ({
                    ...i,
                    splits: [
                      ...(i.splits || []),
                      { amount: "", method: "cash", receiver: "" },
                    ],
                  }));
                };

                const removeSplit = (idx) => {
                  setTxnInputs((i) => {
                    const next = [...(i.splits || [])];
                    next.splice(idx, 1);
                    return { ...i, splits: next };
                  });
                };

                return (
                  <form
                    className="game-form-vertical"
                    onSubmit={handleTransactionSubmit}
                  >
                    {/* Type Selection */}
                    <div className="game-form-row">
                      <select
                        className="game-input"
                        value={txnInputs.type || ""}
                        onChange={(e) => updateType(e.target.value)}
                        required
                      >
                        <option value="" disabled>
                          Select type
                        </option>
                        {!txnForm.fromX && (
                          <option value="deposit">Deposit</option>
                        )}
                        <option value="withdrawal">Withdrawal</option>
                        {modalOwed > 0 && (
                          <option value="debt">Clear Session Credit</option>
                        )}
                        {modalRunning !== 0 && (
                          <option value="account_balance">
                            {modalRunning > 0
                              ? "Clear Balance"
                              : "Balance Payout"}
                          </option>
                        )}
                        <option value="bonus">Bonus</option>
                      </select>

                      {isDeposit && (
                        <label className="game-checkbox">
                          <input
                            type="checkbox"
                            checked={!!txnInputs.credit}
                            onChange={(e) => updateCredit(e.target.checked)}
                          />
                          <span
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ lineHeight: "1.2" }}>
                              Session Credit
                            </span>
                          </span>
                        </label>
                      )}

                      {canSettle && (
                        <label className="game-checkbox">
                          <input
                            type="checkbox"
                            checked={!!txnInputs.settle}
                            onChange={(e) => updateSettle(e.target.checked)}
                          />
                          <span
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ lineHeight: "1.2" }}>Settle</span>
                          </span>
                        </label>
                      )}
                    </div>

                    {isBonus && (
                      <div
                        style={{
                          display: "flex",
                          gap: "0.75rem",
                          marginBottom: "0.25rem",
                        }}
                      >
                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.625rem",
                            padding: "0.75rem 1rem",
                            border: `2px solid ${txnInputs.method !== "book" ? "#d69e2e" : "var(--game-border)"}`,
                            borderRadius: "var(--game-radius)",
                            cursor: "pointer",
                            background:
                              txnInputs.method !== "book"
                                ? "rgba(214,158,46,0.1)"
                                : "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={txnInputs.method !== "book"}
                            onChange={() => updateMethod("")}
                            style={{
                              width: 18,
                              height: 18,
                              accentColor: "#d69e2e",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ fontWeight: 600, color: "#d69e2e" }}>
                            Chips
                          </span>
                        </label>

                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.625rem",
                            padding: "0.75rem 1rem",
                            border: `2px solid ${txnInputs.method === "book" ? "var(--game-success)" : "var(--game-border)"}`,
                            borderRadius: "var(--game-radius)",
                            cursor: "pointer",
                            background:
                              txnInputs.method === "book"
                                ? "rgba(72,187,120,0.1)"
                                : "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={txnInputs.method === "book"}
                            onChange={() => updateMethod("book")}
                            style={{
                              width: 18,
                              height: 18,
                              accentColor: "var(--game-success)",
                              cursor: "pointer",
                            }}
                          />
                          <span
                            style={{
                              fontWeight: 600,
                              color: "var(--game-success)",
                            }}
                          >
                            Account Balance
                          </span>
                        </label>
                      </div>
                    )}

                    {isBonus && txnInputs.method === "book" && (
                      <p
                        style={{
                          fontSize: "0.8em",
                          color: "var(--game-text-secondary)",
                          margin: "0 0 0.25rem",
                        }}
                      >
                        The bonus will go towards player's account balance.
                      </p>
                    )}
                    {isBonus && txnInputs.method !== "book" && (
                      <p
                        style={{
                          fontSize: "0.8em",
                          color: "var(--game-text-secondary)",
                          margin: "0 0 0.25rem",
                        }}
                      >
                        The player will receive the bonus in chips.
                      </p>
                    )}

                    {/* Amount + Bust */}
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "stretch",
                      }}
                    >
                      <input
                        type="number"
                        className="game-input"
                        ref={txnAmountInputRef}
                        style={{ flex: 1 }}
                        step="1"
                        min="0"
                        placeholder={
                          isDebtPayback
                            ? `Max ${modalOwed}`
                            : isAccountBalance
                              ? `Max ${Math.abs(modalRunning)}`
                              : isWithdrawal
                                ? "Total Chips"
                                : "Amount"
                        }
                        value={txnInputs.amount ?? ""}
                        onChange={(e) => updateAmount(e.target.value)}
                        onWheel={preventModalNumberWheel}
                        required
                      />
                      {isWithdrawal && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            padding: "0.6rem 0.85rem",
                            border: `2px solid ${String(txnInputs.amount) === "0" ? "var(--game-danger)" : "var(--game-border)"}`,
                            borderRadius: "var(--game-radius)",
                            cursor: "pointer",
                            background:
                              String(txnInputs.amount) === "0"
                                ? "rgba(245,101,101,0.12)"
                                : "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={String(txnInputs.amount) === "0"}
                            onChange={() =>
                              updateAmount(
                                String(txnInputs.amount) === "0" ? "" : "0",
                              )
                            }
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: "var(--game-danger)",
                              cursor: "pointer",
                            }}
                          />
                          <span
                            style={{
                              fontWeight: 600,
                              color: "var(--game-danger)",
                              fontSize: "0.875rem",
                            }}
                          >
                            Bust
                          </span>
                        </label>
                      )}
                    </div>

                    {/* Method */}
                    {showMethodSelect && Number(txnInputs.amount) > 0 && (
                      <select
                        className="game-input"
                        value={txnInputs.method || ""}
                        onChange={(e) => updateMethod(e.target.value)}
                        required
                      >
                        <option value="" hidden>
                          Method
                        </option>
                        <option value="cash">Cash</option>
                        {(isDebtPayback ||
                          (isAccountBalance && modalRunning > 0)) && (
                          <option value="chips">Chips</option>
                        )}
                        {((!isAccountBalance &&
                          txnInputs.amount > modalRunning &&
                          modalRunning < 0 &&
                          txnInputs.amount <= modalRunning * -1) ||
                          (isWithdrawal &&
                            (!isSettling || amountNum > modalOwed))) && (
                          <option value="book">Player Balance</option>
                        )}
                        <option value="venmo">Venmo</option>
                        <option value="cashapp">CashApp</option>
                        <option value="bitcoin">Bitcoin</option>
                        <option value="zelle">Zelle</option>
                        <option value="applePay">Apple Pay</option>
                        <option value="paypal">PayPal</option>
                        {txnInputs.type === "withdrawal" && (
                          <option value="mixed">Mixed</option>
                        )}
                      </select>
                    )}

                    {/* Credit */}
                    {txnInputs.type === "deposit" &&
                      txnInputs.credit === true &&
                      (() => {
                        const amount = Number(txnInputs.amount || 0);

                        return (
                          <div
                            style={{
                              marginTop: "1rem",
                              padding: "1rem",
                              background: "var(--game-bg-tertiary)",
                              border: "1px solid var(--game-border)",
                              borderRadius: "var(--game-radius)",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.875rem",
                                color: "var(--game-text-secondary)",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {exceedLimit
                                ? "Cannot Exceed Credit Limit"
                                : "New Session Credit"}
                            </div>
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "700",
                                color: exceedLimit
                                  ? "var(--game-danger)"
                                  : "var(--game-warning)",
                              }}
                            >
                              <span>
                                {availableCredit > 0
                                  ? `$${modalOwed + amount} / $${availableCredit + modalOwed}`
                                  : `$${modalOwed + amount}`}
                              </span>
                            </div>
                          </div>
                        );

                        return null;
                      })()}

                    {/* Bonus actual new debt */}
                    {(txnInputs.type === "account_balance" ||
                      (txnInputs.type === "bonus" &&
                        txnInputs.method === "book")) &&
                      (() => {
                        const amount = Number(txnInputs.amount || 0);

                        return (
                          <div
                            style={{
                              marginTop: "1rem",
                              padding: "1rem",
                              background: "var(--game-bg-tertiary)",
                              border: "1px solid var(--game-border)",
                              borderRadius: "var(--game-radius)",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.875rem",
                                color: "var(--game-text-secondary)",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {modalRunning - amount < 0
                                ? "New Player Balance"
                                : "New Player Debt"}
                            </div>
                            <div
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: "700",
                                color:
                                  modalRunning - amount < 0
                                    ? "var(--game-success)"
                                    : "var(--game-danger)",
                              }}
                            >
                              ${Math.abs(modalRunning - amount)}
                            </div>
                          </div>
                        );

                        return null;
                      })()}

                    {/* Actual Payment Display */}
                    {txnInputs.type === "withdrawal" &&
                      txnInputs.method !== "mixed" &&
                      txnInputs.method !== "book" &&
                      (() => {
                        const amount = Number(txnInputs.amount || 0);
                        let deduction = 0;
                        let label = "";

                        if (txnInputs.settle && Number(modalOwed) > 0) {
                          deduction = Number(modalOwed);
                          label = "Debt";
                        } else if (Number(modalRunning) < 0) {
                          deduction = Math.abs(Number(modalRunning));
                          label = "Credit";
                        }

                        if (deduction > 0 && amount > deduction) {
                          return (
                            <div
                              style={{
                                marginTop: "1rem",
                                padding: "1rem",
                                background: "var(--game-bg-tertiary)",
                                border: "1px solid var(--game-border)",
                                borderRadius: "var(--game-radius)",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.875rem",
                                  color: "var(--game-text-secondary)",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                Actual Payment
                              </div>
                              <div
                                style={{
                                  fontSize: "1.5rem",
                                  fontWeight: "700",
                                  color: "var(--game-success)",
                                }}
                              >
                                ${amount - deduction}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                    {/* Mixed Splits */}
                    {isMixed && (
                      <div className="game-splits">
                        <div className="game-splits__remaining">
                          <strong>Remaining:</strong> $
                          {payoutTarget - sumSplits}
                          {payoutTarget - sumSplits === 0 && " ✅"}
                        </div>

                        {chipsUsed > 0 && (
                          <div className="game-split-row game-split-row--readonly">
                            <input
                              type="number"
                              className="game-input"
                              value={chipsUsed}
                              disabled
                              readOnly
                              onWheel={preventModalNumberWheel}
                            />
                            <select
                              className="game-input"
                              value="chips"
                              disabled
                            >
                              <option value="chips">Chips</option>
                            </select>
                            <input
                              className="game-input"
                              value="None"
                              disabled
                              readOnly
                            />
                            <div />
                          </div>
                        )}

                        {splits.map((row, idx) => (
                          <>
                            <input
                              type="number"
                              className="game-input"
                              step="1"
                              min="0"
                              placeholder="Amount"
                              value={row.amount}
                              onChange={(e) =>
                                updateSplit(
                                  idx,
                                  "amount",
                                  e.target.value.replace(/[^\d.]/g, ""),
                                )
                              }
                              onWheel={preventModalNumberWheel}
                            />

                            <select
                              className="game-input"
                              value={row.method || ""}
                              onChange={(e) =>
                                updateSplit(idx, "method", e.target.value)
                              }
                            >
                              <option value="" hidden>
                                Method
                              </option>
                              <option value="cash">Cash</option>
                              <option value="venmo">Venmo</option>
                              <option value="cashapp">CashApp</option>
                              <option value="bitcoin">Bitcoin</option>
                              <option value="zelle">Zelle</option>
                              <option value="applePay">Apple Pay</option>
                              <option value="paypal">PayPal</option>
                              <option value="book">Player Balance</option>
                            </select>

                            <select
                              className="game-input"
                              value={row.receiver || ""}
                              onChange={(e) =>
                                updateSplit(idx, "receiver", e.target.value)
                              }
                              disabled={!needsWallet(row.method)}
                            >
                              <option value="" hidden>
                                {needsWallet(row.method) ? "Wallet" : "None"}
                              </option>
                              {wallets.length > 0 ? (
                                wallets.map((w) => (
                                  <option key={w.id} value={w.id}>
                                    {w.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>None</option>
                              )}
                            </select>

                            <button
                              type="button"
                              className="game-icon-btn game-icon-btn--danger"
                              onClick={() => removeSplit(idx)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ))}

                        <button
                          type="button"
                          className="game-btn game-btn--sm game-btn--ghost"
                          onClick={addSplit}
                          disabled={payoutTarget <= sumSplits}
                        >
                          + Add Split
                        </button>
                      </div>
                    )}

                    {/* Receiver */}
                    {needReceiver && (
                      <select
                        className="game-input"
                        value={txnInputs.receiver || ""}
                        onChange={(e) => updateReceiver(e.target.value)}
                        required
                      >
                        <option value="" hidden>
                          {isDeposit || isDebtPayback
                            ? "To"
                            : isAccountBalance && modalRunning > 0
                              ? "To"
                              : "From"}
                        </option>
                        {wallets.length > 0 ? (
                          wallets.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))
                        ) : (
                          <option disabled>None</option>
                        )}
                      </select>
                    )}

                    {/* Roll Debt Checkbox */}
                    {canRollDebt && (
                      <label
                        className="game-checkbox"
                        style={{ alignItems: "flex-start" }}
                      >
                        <input
                          type="checkbox"
                          checked={!!txnInputs.payLater}
                          onChange={(e) =>
                            setTxnInputs((prev) => ({
                              ...prev,
                              payLater: e.target.checked,
                            }))
                          }
                          style={{ marginTop: "4px" }}
                        />
                        <span
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span style={{ lineHeight: "1.2" }}>
                            Roll balance
                          </span>
                          <span
                            style={{
                              fontSize: "0.8em",
                              color: "var(--game-text-secondary)",
                              marginTop: "2px",
                            }}
                          >
                            Keep remaining session credit as debt
                          </span>
                        </span>
                      </label>
                    )}

                    {/* Actions */}
                    <div className="game-modal__footer">
                      <button
                        type="button"
                        className="game-btn game-btn--ghost"
                        onClick={() =>
                          setTxnForm({ tableId: null, playerId: null })
                        }
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="game-btn game-btn--primary"
                        disabled={submitDisabled}
                      >
                        Submit
                      </button>
                    </div>

                    {/* Debt Reduce Section */}
                    {!txnInputs.credit &&
                      txnInputs.type === "withdrawal" &&
                      txnInputs.settle &&
                      !txnInputs.payLater &&
                      Number(txnInputs.amount || 0) >= 0 &&
                      Number(txnInputs.amount || 0) < modalOwed &&
                      modalPlayer && (
                        <div className="game-debt-reduce">
                          <h4 className="game-debt-reduce__title">
                            Clear Remaining Session Credit
                          </h4>
                          <div className="game-form-vertical">
                            <input
                              type="number"
                              className="game-input"
                              value={debtAmounts[modalPlayer.id] || ""}
                              placeholder={`Max ${
                                modalOwed - Number(txnInputs.amount || 0)
                              }`}
                              step="1"
                              min="0"
                              max={modalOwed - Number(txnInputs.amount || 0)}
                              onChange={(e) =>
                                setDebtAmounts((prev) => ({
                                  ...prev,
                                  [modalPlayer.id]: e.target.value,
                                }))
                              }
                              onWheel={preventModalNumberWheel}
                              required
                            />

                            <select
                              className="game-input"
                              value={methodAmounts[modalPlayer.id] || "cash"}
                              onChange={(e) =>
                                setMethodAmounts((prev) => ({
                                  ...prev,
                                  [modalPlayer.id]: e.target.value,
                                }))
                              }
                              required
                            >
                              <option value="" hidden>
                                Method
                              </option>
                              <option value="cash">Cash</option>
                              {debtAmounts[modalPlayer.id] > modalRunning &&
                                modalRunning < 0 &&
                                debtAmounts[modalPlayer.id] <=
                                  modalRunning * -1 && (
                                  <option value="book">Player Balance</option>
                                )}
                              <option value="venmo">Venmo</option>
                              <option value="cashapp">CashApp</option>
                              <option value="bitcoin">Bitcoin</option>
                              <option value="zelle">Zelle</option>
                              <option value="applePay">Apple Pay</option>
                              <option value="paypal">PayPal</option>
                            </select>

                            {!["cash", "chips", "book", ""].includes(
                              methodAmounts[modalPlayer.id] ?? "cash",
                            ) && (
                              <select
                                className="game-input"
                                value={clearReceivers[modalPlayer.id] || ""}
                                onChange={(e) =>
                                  setClearReceivers((prev) => ({
                                    ...prev,
                                    [modalPlayer.id]: e.target.value,
                                  }))
                                }
                                required
                              >
                                <option value="" hidden>
                                  To
                                </option>
                                {wallets.length > 0 ? (
                                  wallets.map((w) => (
                                    <option key={w.id} value={w.id}>
                                      {w.name}
                                    </option>
                                  ))
                                ) : (
                                  <option disabled>None</option>
                                )}
                              </select>
                            )}

                            <button
                              type="button" // <- important: no submit here
                              className="game-btn game-btn--primary game-btn--sm"
                              onClick={() =>
                                handleAdditionalPayment(modalPlayer)
                              }
                              disabled={
                                !debtAmounts[modalPlayer.id] ||
                                Number(debtAmounts[modalPlayer.id]) <= 0 ||
                                Number(debtAmounts[modalPlayer.id]) >
                                  modalOwed - Number(txnInputs.amount || 0) ||
                                ((methodAmounts[modalPlayer.id] || "cash") !== "cash" &&
                                  (methodAmounts[modalPlayer.id] || "cash") !== "book" &&
                                  !clearReceivers[modalPlayer.id]) ||
                                isSubmitting
                              }
                            >
                              Submit Payment
                            </button>
                          </div>
                        </div>
                      )}
                  </form>
                );
              })()}
            </div>
          </div>
        </>
      )}
      {/* Pay Dealer Modal */}
      {payingDealer && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setPayingDealer(null)}
          />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Pay {payingDealer.name}</h3>
              <span className="game-badge game-badge--running">
                Owed ${payingDealer.owed}
              </span>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handlePayDealer}>
                {/* Amount */}
                <input
                  type="number"
                  className="game-input"
                  ref={payDealerAmountInputRef}
                  value={payAmount}
                  placeholder={`Max $${payingDealer.owed}`}
                  step="1"
                  max={payingDealer.owed}
                  onChange={(e) => setPayAmount(e.target.value)}
                  onWheel={preventModalNumberWheel}
                  required
                />

                {/* Method */}
                <select
                  className="game-input"
                  value={payMethod}
                  onChange={(e) => {
                    const m = e.target.value;
                    setPayMethod(m);
                    if (["cash", "chips", "", "mixed"].includes(m)) {
                      setPayReceiver("");
                    }
                    if (m === "mixed") {
                      setPaySplits((rows) =>
                        rows && rows.length > 0
                          ? rows
                          : [{ amount: "", method: "cash", receiver: "" }],
                      );
                    } else {
                      setPaySplits([]);
                    }
                  }}
                  required
                >
                  <option value="" hidden>
                    Method
                  </option>
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="zelle">Zelle</option>
                  <option value="applePay">Apple Pay</option>
                  <option value="paypal">PayPal</option>
                  <option value="mixed">Mixed</option>
                </select>

                {/* Mixed Splits */}
                {payMethod === "mixed" &&
                  (() => {
                    const totalTarget = Math.min(
                      Number(payAmount || 0),
                      Number(payingDealer.owed || 0),
                    );
                    const needsWallet = (m) =>
                      m && !["cash", "chips"].includes(m);
                    const sumSplits = (paySplits || []).reduce(
                      (s, r) => s + (Number(r.amount || 0) || 0),
                      0,
                    );
                    const remaining = totalTarget - sumSplits;

                    const updateSplit = (idx, field, value) => {
                      setPaySplits((arr) => {
                        const next = [...arr];
                        next[idx] = { ...next[idx], [field]: value };
                        if (field === "method" && !needsWallet(value))
                          next[idx].receiver = "";
                        return next;
                      });
                    };

                    const addSplit = () =>
                      setPaySplits((arr) => [
                        ...arr,
                        { amount: "", method: "cash", receiver: "" },
                      ]);

                    const removeSplit = (idx) =>
                      setPaySplits((arr) => {
                        const next = [...arr];
                        next.splice(idx, 1);
                        return next;
                      });

                    return (
                      <div className="game-splits">
                        <div className="game-splits__remaining">
                          <strong>Remaining:</strong> ${remaining}
                          {remaining === 0 && " ✅"}
                        </div>

                        {(paySplits || []).map((row, idx) => (
                          <div key={idx} className="game-split-row">
                            <input
                              type="number"
                              className="game-input"
                              step="1"
                              min="0"
                              placeholder="Amount"
                              value={row.amount}
                              onChange={(e) =>
                                updateSplit(
                                  idx,
                                  "amount",
                                  e.target.value.replace(/[^\d.]/g, ""),
                                )
                              }
                              onWheel={preventModalNumberWheel}
                            />

                            <select
                              className="game-input"
                              value={row.method || ""}
                              onChange={(e) =>
                                updateSplit(idx, "method", e.target.value)
                              }
                            >
                              <option value="" hidden>
                                Method
                              </option>
                              <option value="cash">Cash</option>
                              <option value="venmo">Venmo</option>
                              <option value="cashapp">CashApp</option>
                              <option value="bitcoin">Bitcoin</option>
                              <option value="zelle">Zelle</option>
                              <option value="applePay">Apple Pay</option>
                              <option value="paypal">PayPal</option>
                            </select>

                            <select
                              className="game-input"
                              value={row.receiver || ""}
                              onChange={(e) =>
                                updateSplit(idx, "receiver", e.target.value)
                              }
                              disabled={!needsWallet(row.method)}
                            >
                              <option value="" hidden>
                                {needsWallet(row.method) ? "Wallet" : "None"}
                              </option>
                              {wallets.length > 0 ? (
                                wallets.map((w) => (
                                  <option key={w.id} value={w.id}>
                                    {w.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>None</option>
                              )}
                            </select>

                            <button
                              type="button"
                              className="game-icon-btn game-icon-btn--danger"
                              onClick={() => removeSplit(idx)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="game-btn game-btn--sm game-btn--ghost"
                          onClick={addSplit}
                          disabled={remaining <= 0}
                        >
                          + Add Split
                        </button>
                      </div>
                    );
                  })()}

                {/* Receiver (non-cash/chips/mixed) */}
                {!["cash", "chips", "", "mixed"].includes(payMethod) && (
                  <select
                    className="game-input"
                    value={payReceiver}
                    onChange={(e) => setPayReceiver(e.target.value)}
                    required
                  >
                    <option value="" hidden>
                      From
                    </option>
                    {wallets.length > 0 ? (
                      wallets.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>None</option>
                    )}
                  </select>
                )}

                {/* Actions */}
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setPayingDealer(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting ||
                      !payAmount ||
                      Number(payAmount) <= 0 ||
                      Number(payAmount) > Number(payingDealer.owed) ||
                      !payMethod ||
                      (payMethod === "mixed" &&
                        Array.isArray(paySplits) &&
                        paySplits.every((r) => Number(r?.amount) < 1)) ||
                      (payMethod !== "mixed" &&
                        !["cash", "chips"].includes(payMethod) &&
                        !payReceiver)
                    }
                  >
                    Pay Dealer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
      {/* Quick Add Player Modal */}
      {showQuickAddModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={closeQuickAddPlayerModal}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Quick Add Player</h3>
              <button
                className="game-icon-btn"
                onClick={closeQuickAddPlayerModal}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={handleQuickAddPlayer}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  {/* First Name */}
                  <div>
                    <label className="game-label">First Name *</label>
                    <input
                      className="game-input"
                      type="text"
                      value={qFirstName}
                      onChange={(e) => setQFirstName(e.target.value)}
                      placeholder="First name"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="game-label">Last Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={qLastName}
                      onChange={(e) => setQLastName(e.target.value)}
                      placeholder="Last name"
                    />
                  </div>

                  {/* Nickname */}
                  <div>
                    <label className="game-label">Nickname</label>
                    <input
                      className="game-input"
                      type="text"
                      value={qNickname}
                      onChange={(e) => setQNickname(e.target.value)}
                      placeholder={
                        getDefaultPlayerNickname(qFirstName, qLastName)
                      }
                      maxLength={64}
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Referred By Player</label>
                    <Select
                      options={referralPlayerOptions}
                      value={
                        referralPlayerOptions.find(
                          (option) =>
                            Number(option.value) ===
                            Number(qReferredByPlayerId),
                        ) || null
                      }
                      onChange={(option) =>
                        setQReferredByPlayerId(
                          option ? String(option.value) : "",
                        )
                      }
                      placeholder="No referrer"
                      isClearable
                      isSearchable
                      filterOption={filterPlayerOption}
                      formatOptionLabel={formatPlayerOptionLabel}
                      className="game-select"
                      classNamePrefix="select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          borderColor: "#4a5568",
                          minHeight: "42px",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          zIndex: 9999,
                        }),
                        option: (base, { isFocused }) => ({
                          ...base,
                          backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                          color: "#e2e8f0",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                        input: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                      }}
                    />
                  </div>

                  {/* Book */}
                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Assigned Book</label>
                    <select
                      className="game-input"
                      value={qBookId}
                      onChange={(e) => setQBookId(e.target.value)}
                    >
                      <option value="house">House</option>
                      {books.map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Phone */}
                  <div style={{ minWidth: 0 }}>
                    <label className="game-label">Phone Number</label>
                    <input
                      className="game-input"
                      type="text"
                      value={qPhone}
                      onChange={(e) =>
                        setQPhone(formatPhoneNumber(e.target.value))
                      }
                      placeholder="XXX-XXX-XXXX"
                      maxLength={12}
                    />
                  </div>
                </div>

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={closeQuickAddPlayerModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting || quickAddBtnDisabled}
                  >
                    {quickAddBtnDisabled && qFirstName.trim()
                      ? "Name / Nickname Taken"
                      : "Create Player"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Edit Player Modal */}
      {editingPlayer && (
        <>
          <div className="game-modal-overlay" onClick={resetEditPlayerForm} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Edit Player: {editingPlayer.name}
              </h3>
              {editingPlayer.book > 0 && (
                <span className="game-player-badge game-player-badge--running">
                  Book ${editingPlayer.book}
                </span>
              )}
              {editingPlayer.book < 0 && (
                <span className="game-player-badge game-player-badge--credit">
                  Book ${Math.abs(editingPlayer.book)}
                </span>
              )}
              <button className="game-icon-btn" onClick={resetEditPlayerForm}>
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handleEditPlayer}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <div>
                    <label className="game-label">First Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      placeholder={editingPlayer.first_name || "First name"}
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="game-label">Last Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      placeholder={editingPlayer.last_name || "Last name"}
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="game-label">Nickname</label>
                    <input
                      className="game-input"
                      type="text"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      placeholder={editingPlayer.name}
                      maxLength={64}
                    />
                  </div>

                  <div>
                    <label className="game-label">Referred By Player</label>
                    <Select
                      options={editReferralPlayerOptions}
                      value={
                        editReferralPlayerOptions.find(
                          (option) =>
                            Number(option.value) ===
                            Number(editReferredByPlayerId),
                        ) || null
                      }
                      onChange={(option) =>
                        setEditReferredByPlayerId(
                          option ? String(option.value) : "",
                        )
                      }
                      placeholder="No referrer"
                      isClearable
                      isSearchable
                      isDisabled={referralLocked}
                      filterOption={filterPlayerOption}
                      formatOptionLabel={formatPlayerOptionLabel}
                      className="game-select"
                      classNamePrefix="select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          borderColor: state.isDisabled ? "#374151" : "#4a5568",
                          minHeight: "42px",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          zIndex: 9999,
                        }),
                        option: (base, { isFocused }) => ({
                          ...base,
                          backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                          color: "#e2e8f0",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                        input: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                      }}
                    />
                    {referralLocked && (
                      <small className="game-text-muted">
                        Referral is locked after deposits.
                      </small>
                    )}
                  </div>

                  <div>
                    <label className="game-label">Book Amount</label>
                    <input
                      className="game-input"
                      type="number"
                      step="1"
                      value={editDebt}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || v === "-" || /^-?\d+$/.test(v)) {
                          setEditDebt(v);
                        }
                      }}
                      onWheel={preventModalNumberWheel}
                      placeholder="Negative or positive"
                    />
                    <small className="game-text-muted">
                      Negative = house owes player
                    </small>
                  </div>

                  <div>
                    <label className="game-label">Phone Number</label>
                    <input
                      className="game-input"
                      type="text"
                      value={editPhone}
                      onChange={(e) =>
                        setEditPhone(formatPhoneNumber(e.target.value))
                      }
                      placeholder="XXX-XXX-XXXX"
                      maxLength={12}
                    />
                  </div>

                  <div style={{ gridColumn: "1 / -1", width: "100%" }}>
                    <label className="game-label">Player Notes</label>
                    <textarea
                      className="game-input"
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        resize: "none",
                      }}
                      value={editNotes}
                      onChange={(e) =>
                        setEditNotes(e.target.value.slice(0, 255))
                      }
                      placeholder="Add player notes"
                      maxLength={255}
                      rows={4}
                    />
                    <small className="game-text-muted">
                      {editNotes.length}/255 characters
                    </small>
                  </div>
                </div>

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={resetEditPlayerForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={(() => {
                      if (isSubmitting) return true;
                      const origPhone = editingPlayer.phone_number
                        ? editingPlayer.phone_number.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "$1-$2-$3",
                          )
                        : "";
                      const origReferredByPlayerId =
                        editingPlayer.referred_by_player_id
                          ? String(editingPlayer.referred_by_player_id)
                          : "";
                      const anyChanged =
                        editFirstName !== (editingPlayer.first_name || "") ||
                        editLastName !== (editingPlayer.last_name || "") ||
                        editDisplayName !== "" ||
                        editDebt !== "" ||
                        editPhone !== origPhone ||
                        (!isPlayerReferralLocked(editingPlayer) &&
                          editReferredByPlayerId !==
                            origReferredByPlayerId) ||
                        editNotes !== (editingPlayer.notes || "");
                      return !anyChanged;
                    })()}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Phone Lookup Modal */}
      {showPhoneLookupModal && (
        <>
          <div className="game-modal-overlay" onClick={closePhoneLookupModal} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Phone Number Lookup</h3>
              <button className="game-icon-btn" onClick={closePhoneLookupModal}>
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <label className="game-label">Phone Number</label>
              <div className="phone-lookup-digits" aria-label="Phone number">
                {Array.from({ length: 10 }).map((_, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      phoneLookupInputRefs.current[index] = el;
                    }}
                    className="phone-lookup-digit"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={phoneLookupDigits[index] || ""}
                    onChange={(e) =>
                      handlePhoneLookupDigitChange(index, e.target.value)
                    }
                    onFocus={() => handlePhoneLookupDigitFocus(index)}
                    onKeyDown={(e) => handlePhoneLookupDigitKeyDown(index, e)}
                    onPaste={(e) => handlePhoneLookupPaste(index, e)}
                    aria-label={`Phone digit ${index + 1}`}
                  />
                ))}
              </div>

              {phoneLookupDigits.length > 0 && phoneLookupDigits.length < 10 && (
                <p className="phone-lookup-status">
                  Enter {10 - phoneLookupDigits.length} more digit
                  {10 - phoneLookupDigits.length === 1 ? "" : "s"}.
                </p>
              )}

              {phoneLookupDigits.length === 10 && phoneLookupMatch && (
                <div className="phone-lookup-result phone-lookup-result--match">
                  <div className="phone-lookup-result__eyebrow">
                    Player Found
                  </div>
                  <dl className="phone-lookup-result__details">
                    <div>
                      <dt>Name</dt>
                      <dd>{phoneLookupMatch.name || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>First Name</dt>
                      <dd>{phoneLookupMatch.first_name || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>Last Name</dt>
                      <dd>{phoneLookupMatch.last_name || "Not set"}</dd>
                    </div>
                  </dl>
                  {renderLookupPlayerActions(phoneLookupMatch)}
                </div>
              )}

              {phoneLookupDigits.length === 10 && !phoneLookupMatch && (
                <div className="phone-lookup-result phone-lookup-result--empty">
                  No player matches {formatPhoneNumber(phoneLookupDigits)}.
                </div>
              )}
            </div>

            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setPhoneLookupDigits("")}
                disabled={!phoneLookupDigits}
              >
                Clear
              </button>
              <button
                type="button"
                className="game-btn game-btn--primary"
                onClick={closePhoneLookupModal}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Code Lookup Modal */}
      {showCodeLookupModal && (
        <>
          <div className="game-modal-overlay" onClick={closeCodeLookupModal} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Player Code Lookup</h3>
              <button className="game-icon-btn" onClick={closeCodeLookupModal}>
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <label className="game-label">Player Code</label>
              <div className="phone-lookup-digits" aria-label="Player code">
                {Array.from({ length: 5 }).map((_, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      codeLookupInputRefs.current[index] = el;
                    }}
                    className="phone-lookup-digit"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={codeLookupDigits[index] || ""}
                    onChange={(e) =>
                      handleCodeLookupDigitChange(index, e.target.value)
                    }
                    onFocus={() => handleCodeLookupDigitFocus(index)}
                    onKeyDown={(e) => handleCodeLookupDigitKeyDown(index, e)}
                    onPaste={(e) => handleCodeLookupPaste(index, e)}
                    aria-label={`Player code digit ${index + 1}`}
                  />
                ))}
              </div>

              {codeLookupDigits.length > 0 && codeLookupDigits.length < 5 && (
                <p className="phone-lookup-status">
                  Enter {5 - codeLookupDigits.length} more digit
                  {5 - codeLookupDigits.length === 1 ? "" : "s"}.
                </p>
              )}

              {codeLookupDigits.length === 5 && codeLookupMatch && (
                <div className="phone-lookup-result phone-lookup-result--match">
                  <div className="phone-lookup-result__eyebrow">
                    Player Found
                  </div>
                  <dl className="phone-lookup-result__details">
                    <div>
                      <dt>Name</dt>
                      <dd>{codeLookupMatch.name || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>First Name</dt>
                      <dd>{codeLookupMatch.first_name || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>Last Name</dt>
                      <dd>{codeLookupMatch.last_name || "Not set"}</dd>
                    </div>
                  </dl>
                  {renderLookupPlayerActions(codeLookupMatch)}
                </div>
              )}

              {codeLookupDigits.length === 5 && !codeLookupMatch && (
                <div className="phone-lookup-result phone-lookup-result--empty">
                  No player matches code {codeLookupDigits}.
                </div>
              )}
            </div>

            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setCodeLookupDigits("")}
                disabled={!codeLookupDigits}
              >
                Clear
              </button>
              <button
                type="button"
                className="game-btn game-btn--primary"
                onClick={closeCodeLookupModal}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal: Link to Existing Player */}
      {linkingRequest && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setLinkingRequest(null)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Link to Existing Player</h3>
            </div>
            <div className="game-modal__body">
              <form onSubmit={linkExisting}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <label className="game-label">
                    Select Existing Player (Unlinked Only)
                  </label>
                  <Select
                    options={(players || [])
                      .filter((p) => !p.global_id)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => ({ value: p.id.toString(), label: p.name }))}
                    isSearchable
                    placeholder="Search Player..."
                    value={
                      selectedExistingPlayerId
                        ? {
                            value: selectedExistingPlayerId,
                            label:
                              players.find(
                                (p) =>
                                  p.id === parseInt(selectedExistingPlayerId),
                              )?.name || "",
                          }
                        : null
                    }
                    onChange={(opt) =>
                      setSelectedExistingPlayerId(opt?.value || "")
                    }
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                        borderColor: "#4a5568",
                        color: "#e2e8f0",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                      }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                        color: "#e2e8f0",
                        cursor: "pointer",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#e2e8f0",
                      }),
                      input: (base) => ({
                        ...base,
                        color: "#e2e8f0",
                      }),
                    }}
                  />
                </div>

                {selectedExistingPlayerId &&
                  (() => {
                    const p = players.find(
                      (x) => x.id === parseInt(selectedExistingPlayerId),
                    );
                    if (!p) return null;

                    const ComparisonRow = ({
                      label,
                      oldVal,
                      newVal,
                      staysSame = false,
                    }) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0.75rem 0",
                          borderBottom: "1px solid var(--game-border)",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--game-text-secondary)",
                              marginBottom: "0.25rem",
                            }}
                          >
                            {label}
                          </div>
                          <div style={{ fontWeight: 600 }}>
                            {oldVal || "None"}
                          </div>
                        </div>

                        <div
                          style={{
                            padding: "0 1rem",
                            color: staysSame
                              ? "var(--game-text-secondary)"
                              : "var(--game-accent)",
                          }}
                        >
                          <FaArrowUp style={{ transform: "rotate(90deg)" }} />
                        </div>

                        <div style={{ flex: 1, textAlign: "right" }}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--game-text-secondary)",
                              marginBottom: "0.25rem",
                            }}
                          >
                            {staysSame ? "Stays Same" : "Update To"}
                          </div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: staysSame
                                ? "inherit"
                                : "var(--game-accent)",
                            }}
                          >
                            {newVal || "None"}
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <div
                        style={{
                          background: "var(--game-bg-tertiary)",
                          borderRadius: "var(--game-radius)",
                          padding: "1rem",
                          marginBottom: "1.5rem",
                          border: "1px solid var(--game-border)",
                        }}
                      >
                        <h4
                          style={{
                            margin: "0 0 1rem 0",
                            fontSize: "0.9rem",
                            color: "var(--game-text-secondary)",
                          }}
                        >
                          DATA COMPARISON
                        </h4>

                        <ComparisonRow
                          label="First Name"
                          oldVal={p.first_name}
                          newVal={linkingRequest.first_name}
                        />
                        <ComparisonRow
                          label="Last Name"
                          oldVal={p.last_name}
                          newVal={linkingRequest.last_name}
                        />
                        <ComparisonRow
                          label="Nickname"
                          oldVal={p.name}
                          newVal={p.name}
                          staysSame={true}
                        />
                        <ComparisonRow
                          label="Phone Number"
                          oldVal={formatPhoneNumber(p.phone_number)}
                          newVal={formatPhoneNumber(
                            linkingRequest.phone_number,
                          )}
                        />

                        {(p.first_name?.toLowerCase() !==
                          linkingRequest.first_name?.toLowerCase() ||
                          p.last_name?.toLowerCase() !==
                            linkingRequest.last_name?.toLowerCase()) && (
                          <div
                            className="game-alert game-alert--warning"
                            style={{ marginTop: "1rem", fontSize: "0.85rem" }}
                          >
                            <b>Note:</b> Existing record name differs from
                            request. The profile will be updated with the new
                            details.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setLinkingRequest(null)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="game-btn game-btn--primary">
                    Confirm Link
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Modal: Link to New Player */}
      {linkingNewRequest && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setLinkingNewRequest(null)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Setup New Linked Player</h3>
              <button
                className="game-icon-btn"
                onClick={() => setLinkingNewRequest(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={linkNew}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  {/* First Name */}
                  <div>
                    <label className="game-label">First Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={linkingNewRequest.first_name}
                      disabled
                      style={{ opacity: 0.7 }}
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="game-label">Last Name</label>
                    <input
                      className="game-input"
                      type="text"
                      value={linkingNewRequest.last_name}
                      disabled
                      style={{ opacity: 0.7 }}
                    />
                  </div>

                  {/* Nickname */}
                  <div>
                    <label className="game-label">Nickname</label>
                    <input
                      className="game-input"
                      type="text"
                      value={newNickname}
                      onChange={(e) => setNewNickname(e.target.value)}
                      placeholder="e.g. John D."
                      required
                      maxLength={64}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="game-label">Phone Number</label>
                    <input
                      className="game-input"
                      type="text"
                      value={formatPhoneNumber(linkingNewRequest.phone_number)}
                      disabled
                      style={{ opacity: 0.7 }}
                    />
                  </div>
                </div>

                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setLinkingNewRequest(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting}
                  >
                    Create & Link
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
